import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getTimelineApi, getCourtsApi, createBookingApi, updateBookingApi, releaseBookingApi, markPaidApi, extendBookingApi, moveBookingApi, updateBookingNotesApi, createOpenPlayApi, getOpenPlayApi, joinOpenPlayApi, removeFromOpenPlayApi, checkinPlayerApi, payOpenPlayApi, cancelOpenPlayApi, updateOpenPlayApi, createCourtBlockApi, removeCourtBlockApi, excludeDateFromSeriesApi, createRecurringBookingApi, markProofReceivedApi, verifyAndConfirmApi, getGlobalAlertsApi, getMediaUrl, createCoachingSessionApi, getCoachingSessionsApi, renameCoachingSessionApi, deleteCoachingSessionApi, approveBookingApi, rejectBookingApi, cleanupOrphanedRecurringApi, createTournamentApi, updateTournamentApi, cancelTournamentApi, getTournamentsApi } from '../lib/api';
import { formatHour, formatPrice, getStatusLabel, getBookingStatusLabel, getTodayPHT, getCurrentHourPHT } from '../lib/format';
import { blockStyles, badgeStyles, buttonStyles, filterStyles, statusStyles } from '../styles/colorTokens';
import { LayoutDashboard, Settings, LogOut, Plus, X, ChevronRight, ChevronLeft, Users, Clock, DollarSign, MapPin, Check, Trash2, UserPlus, UserMinus, CreditCard, Ban, Repeat, Pencil, Copy, CalendarPlus, Wrench, Eye, EyeOff, Timer, ArrowRightLeft, StickyNote, ChevronDown, AlertCircle, Phone, CircleCheck, CircleAlert, RefreshCw, Hourglass, ShieldQuestion, Bell, BarChart3, Calendar, ExternalLink, CalendarDays, GraduationCap, Link2, Trophy, Facebook, Award } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { CountdownTimer, BookingTile, OpenPlayTile, BlockTile } from '../components/operator';
import CountdownTimerInline from '../components/operator/CountdownTimerInline';
import PaymentProofDialog from '../components/operator/PaymentProofDialog';
import EditOpenPlayDialog from '../components/operator/EditOpenPlayDialog';
import BlockCourtDialog from '../components/operator/BlockCourtDialog';
import CreateOpenPlayDialog from '../components/operator/CreateOpenPlayDialog';

export default function OperatorToday() {
  const navigate = useNavigate();
  const { user, loading: authLoading, logout, hasPermission, isAdmin, isOperator, canAccessAdminView, canManageSchedule, canManageCoaching, canApprovePayments } = useAuth();
  const [timelineData, setTimelineData] = useState(null);
  const [selectedDate, setSelectedDate] = useState(() => getTodayPHT());
  const [selectedCell, setSelectedCell] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [createBookingOpen, setCreateBookingOpen] = useState(false);
  const [createOpenPlayOpen, setCreateOpenPlayOpen] = useState(false);
  const [blockCourtOpen, setBlockCourtOpen] = useState(false);
  const [coachingSessionOpen, setCoachingSessionOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  // Quick booking form state
  const [qbName, setQbName] = useState('');
  const [qbPhone, setQbPhone] = useState('');
  const [qbDuration, setQbDuration] = useState('1');
  const [qbBookingType, setQbBookingType] = useState('court'); // 'court' | 'coaching' | 'training'
  const [qbPaymentStatus, setQbPaymentStatus] = useState('paid'); // 'paid' | 'unpaid' | 'free'
  const [qbSpecialRateOn, setQbSpecialRateOn] = useState(false);
  const [qbSpecialRateAmount, setQbSpecialRateAmount] = useState('');
  const [qbIsMember, setQbIsMember] = useState(false);
  const [qbIsSpecialPrivilege, setQbIsSpecialPrivilege] = useState(false);

  // Open play form state
  const [opCourts, setOpCourts] = useState([]);
  const [opDate, setOpDate] = useState(() => getTodayPHT());
  const [opStartHour, setOpStartHour] = useState('');
  const [opEndHour, setOpEndHour] = useState('');
  const [opMaxPlayers, setOpMaxPlayers] = useState('16');
  const [opPrice, setOpPrice] = useState('150');
  const [opWalkins, setOpWalkins] = useState(true);
  const [opAutoClose, setOpAutoClose] = useState(true);

  // Add player to open play
  const [addPlayerName, setAddPlayerName] = useState('');

  // Edit open play - inline mode
  const [editOpenPlayOpen, setEditOpenPlayOpen] = useState(false);
  const [editOpenPlaySession, setEditOpenPlaySession] = useState(null);
  const [editOpStartHour, setEditOpStartHour] = useState('');
  const [editOpEndHour, setEditOpEndHour] = useState('');
  const [editOpMaxPlayers, setEditOpMaxPlayers] = useState('16');
  const [editOpPrice, setEditOpPrice] = useState('150');
  const [opInlineEditMode, setOpInlineEditMode] = useState(false);

  // Block court form state
  const [blockEndHour, setBlockEndHour] = useState('');
  const [blockReason, setBlockReason] = useState('');

  // Payment proof viewer
  const [paymentProofOpen, setPaymentProofOpen] = useState(false);
  const [paymentProofUrl, setPaymentProofUrl] = useState('');
  const [paymentProofPlayer, setPaymentProofPlayer] = useState('');

  // Unified schedule creation
  const [schedulePickerOpen, setSchedulePickerOpen] = useState(false);
  const [filters, setFilters] = useState({ bookings: true, openPlay: true, blocks: true });

  // Standalone booking fields (when creating from Add Schedule, not cell click)
  const [qbCourtId, setQbCourtId] = useState('');
  const [qbStartHour, setQbStartHour] = useState('');

  // Standalone block fields
  const [blockCourtId, setBlockCourtId] = useState('');
  const [blockStartHour, setBlockStartHour] = useState('');

  // Recurring booking form
  const [recurringBookingOpen, setRecurringBookingOpen] = useState(false);
  const [rbPlayerName, setRbPlayerName] = useState('');
  const [rbPlayerEmail, setRbPlayerEmail] = useState('');
  const [rbPlayerPhone, setRbPlayerPhone] = useState('');
  const [rbCourtId, setRbCourtId] = useState('');
  const [rbStartHour, setRbStartHour] = useState('');
  const [rbDuration, setRbDuration] = useState('1');
  const [rbWeekdays, setRbWeekdays] = useState([]);
  const [rbStartDate, setRbStartDate] = useState(() => getTodayPHT());
  const [rbInterval, setRbInterval] = useState('1');
  const [rbSubmitting, setRbSubmitting] = useState(false);

  // Coaching session state
  const [coachingSessions, setCoachingSessions] = useState([]);
  const [csCoachName, setCsCoachName] = useState('');
  const [csCreating, setCsCreating] = useState(false);
  const [csCopiedId, setCsCopiedId] = useState(null);
  const [csRenameId, setCsRenameId] = useState(null);
  const [csRenameValue, setCsRenameValue] = useState('');
  const [csRenaming, setCsRenaming] = useState(false);

  // Tournament state
  const [tournamentOpen, setTournamentOpen] = useState(false);
  const [tName, setTName] = useState('');
  const [tOrganizerName, setTOrganizerName] = useState('');
  const [tOrganizerPhone, setTOrganizerPhone] = useState('');
  const [tOrganizerEmail, setTOrganizerEmail] = useState('');
  const [tCourts, setTCourts] = useState([]);
  const [tStartDate, setTStartDate] = useState(() => getTodayPHT());
  const [tEndDate, setTEndDate] = useState(() => getTodayPHT());
  const [tStartHour, setTStartHour] = useState('6');
  const [tEndHour, setTEndHour] = useState('18');
  const [tAdditionalCharges, setTAdditionalCharges] = useState([]);
  const [tNotes, setTNotes] = useState('');
  const [tSubmitting, setTSubmitting] = useState(false);

  // Tournament edit state (for block detail drawer)
  const [editTournament, setEditTournament] = useState(null);
  const [editTCharges, setEditTCharges] = useState([]);
  const [editTSaving, setEditTSaving] = useState(false);

  // Drawer action state
  const [showNoteInput, setShowNoteInput] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Needs Action dropdown state
  const [needsActionOpen, setNeedsActionOpen] = useState(false);

  // Ops Status Bar filter state
  const [opsFilter, setOpsFilter] = useState(null); // 'needApproval' | 'awaitingPayment' | 'confirmedToday' | 'emptySoon' | null

  // Global Alerts: Cross-day task awareness
  const [globalAlerts, setGlobalAlerts] = useState(null); // { counts: {...}, other_day_bookings: [...] }
  const [crossDayTasksOpen, setCrossDayTasksOpen] = useState(false); // Modal for cross-day task list

  // Context Locking: Track topmost visible hour in schedule viewport
  const [visibleTopHour, setVisibleTopHour] = useState(null);
  const scheduleGridRef = useRef(null);

  // Attention Anchoring: Track focused booking and matches
  const [anchoredBookingId, setAnchoredBookingId] = useState(null);
  const [anchorMatches, setAnchorMatches] = useState([]); // Array of matching booking IDs
  const [anchorIndex, setAnchorIndex] = useState(0); // Current index in matches
  const anchorTimeoutRef = useRef(null);

  // Notification chime: subtle audio feedback for status changes
  const audioCtxRef = useRef(null);

  const playChime = useCallback(() => {
    try {
      if (!audioCtxRef.current) audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      const ctx = audioCtxRef.current;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      osc.frequency.setValueAtTime(1175, ctx.currentTime + 0.08);
      gain.gain.setValueAtTime(0.15, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.3);
    } catch (e) { /* audio not available */ }
  }, []);

  // Attention Anchoring: Handler to scroll to and highlight bookings by status
  const handleAttentionAnchor = (statusFilter) => {
    // Clear any existing anchor timeout
    if (anchorTimeoutRef.current) {
      clearTimeout(anchorTimeoutRef.current);
    }
    
    // Find all matching bookings based on the status filter
    let matches = [];
    switch (statusFilter) {
      case 'actionRequired':
        matches = blockingIssues.map(b => b.id);
        break;
      case 'needApproval':
        matches = bookings.filter(b => b.status === 'pending_approval').map(b => b.id);
        break;
      case 'awaitingPayment':
        matches = bookings.filter(b => b.status === 'pending_payment').map(b => b.id);
        break;
      case 'confirmedToday':
        matches = bookings.filter(b => b.status === 'confirmed').map(b => b.id);
        break;
      default:
        matches = [];
    }
    
    if (matches.length === 0) {
      toast.info('No matching bookings found');
      return;
    }
    
    // Sort by start hour to find the earliest
    const sortedMatches = matches
      .map(id => bookings.find(b => b.id === id))
      .filter(Boolean)
      .sort((a, b) => a.start_hour - b.start_hour)
      .map(b => b.id);
    
    setAnchorMatches(sortedMatches);
    setAnchorIndex(0);
    
    // Get the first matching booking
    const targetId = sortedMatches[0];
    setAnchoredBookingId(targetId);
    
    // Find the DOM element and scroll to it
    const element = document.querySelector(`[data-booking-id="${targetId}"]`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    
    // Clear the anchor highlight after 3 seconds
    anchorTimeoutRef.current = setTimeout(() => {
      setAnchoredBookingId(null);
      setAnchorMatches([]);
      setAnchorIndex(0);
    }, 3000);
  };

  // Navigate to next/prev anchor match
  const handleAnchorNav = (direction) => {
    if (anchorMatches.length <= 1) return;
    
    // Clear existing timeout
    if (anchorTimeoutRef.current) {
      clearTimeout(anchorTimeoutRef.current);
    }
    
    let newIndex;
    if (direction === 'next') {
      newIndex = (anchorIndex + 1) % anchorMatches.length;
    } else {
      newIndex = (anchorIndex - 1 + anchorMatches.length) % anchorMatches.length;
    }
    
    setAnchorIndex(newIndex);
    const targetId = anchorMatches[newIndex];
    setAnchoredBookingId(targetId);
    
    // Scroll to element
    const element = document.querySelector(`[data-booking-id="${targetId}"]`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    
    // Reset timeout
    anchorTimeoutRef.current = setTimeout(() => {
      setAnchoredBookingId(null);
      setAnchorMatches([]);
      setAnchorIndex(0);
    }, 3000);
  };

  // Schedule picker context prefill state
  const [prefillCourtId, setPrefillCourtId] = useState('');
  const [prefillStartHour, setPrefillStartHour] = useState('');
  const [prefillDuration, setPrefillDuration] = useState('1');
  const [releaseConfirmOpen, setReleaseConfirmOpen] = useState(false);
  
  // Open Play release confirmation (inline panel)
  const [releaseOpenPlayConfirmOpen, setReleaseOpenPlayConfirmOpen] = useState(false);
  
  // Cross-day navigation refs (to avoid race conditions with setTimeout)
  const pendingAnchorRef = useRef(null);
  const pendingDrawerBookingRef = useRef(null);

  useEffect(() => {
    // Wait for auth check to complete before redirecting
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    loadTimeline();
  }, [user, authLoading, selectedDate, navigate]);

  // Auto-refresh Open Play data when drawer is open (poll every 10 seconds)
  useEffect(() => {
    if (!drawerOpen || !selectedCell?.openPlay) return;
    
    const intervalId = setInterval(async () => {
      try {
        const res = await getTimelineApi(selectedDate);
        setTimelineData(res.data);
        // Update the selectedCell with fresh Open Play data
        const freshOpenPlay = (res.data.open_play_sessions || []).find(
          op => op.id === selectedCell.openPlay.id
        );
        if (freshOpenPlay) {
          setSelectedCell(prev => ({ ...prev, openPlay: freshOpenPlay }));
        }
      } catch (err) {
        console.error('Failed to refresh:', err);
      }
    }, 10000); // Poll every 10 seconds
    
    return () => clearInterval(intervalId);
  }, [drawerOpen, selectedCell?.openPlay?.id, selectedDate]);

  // Auto-refresh timeline every 10 seconds to catch new bookings and proof uploads
  const prevBookingCountRef = useRef(0);
  const prevProofCountRef = useRef(0);
  const prevNeedApprovalRef = useRef(0);
  const prevAwaitingPaymentRef = useRef(0);
  
  useEffect(() => {
    if (!user) return;
    
    const intervalId = setInterval(async () => {
      try {
        const res = await getTimelineApi(selectedDate);
        const newBookings = res.data.bookings || [];
        const prevCount = prevBookingCountRef.current;
        
        // Compute ops counts from fresh data
        const needApproval = newBookings.filter(b => b.status === 'pending_approval').length;
        const awaitingPayment = newBookings.filter(b => b.status === 'pending_payment').length;
        
        // Play chime if any ops count increased
        if (
          (prevNeedApprovalRef.current > 0 || prevAwaitingPaymentRef.current > 0 || prevCount > 0) &&
          (needApproval > prevNeedApprovalRef.current || awaitingPayment > prevAwaitingPaymentRef.current)
        ) {
          playChime();
        }
        prevNeedApprovalRef.current = needApproval;
        prevAwaitingPaymentRef.current = awaitingPayment;
        
        // Check if new bookings arrived
        if (prevCount > 0 && newBookings.length > prevCount) {
          const newCount = newBookings.length - prevCount;
          toast.info(`${newCount} new booking${newCount > 1 ? 's' : ''} received`, {
            description: 'Schedule updated',
            duration: 4000
          });
          playChime();
        }
        prevBookingCountRef.current = newBookings.length;
        
        // Check if new payment proofs were uploaded
        const proofsNeedingApproval = newBookings.filter(b => 
          b.status === 'pending_approval' && b.payment_proof
        ).length;
        
        if (prevProofCountRef.current > 0 && proofsNeedingApproval > prevProofCountRef.current) {
          toast.warning('New payment proof uploaded', {
            description: 'A player has submitted payment proof for approval',
            duration: 5000
          });
        }
        prevProofCountRef.current = proofsNeedingApproval;
        
        setTimelineData(res.data);
        
        // If drawer is open with a booking, update it with fresh data
        if (drawerOpen && selectedCell?.booking) {
          const freshBooking = newBookings.find(
            b => b.id === selectedCell.booking.id
          );
          if (freshBooking) {
            setSelectedCell(prev => ({ ...prev, booking: freshBooking }));
          }
        }
      } catch (err) {
        console.error('Failed to auto-refresh timeline:', err);
      }
    }, 10000); // Poll every 10 seconds for faster updates
    
    return () => clearInterval(intervalId);
  }, [user, selectedDate, drawerOpen, selectedCell?.booking?.id]);

  // Context Locking: Detect topmost visible hour on scroll
  useEffect(() => {
    const handleScroll = () => {
      if (!scheduleGridRef.current) return;
      
      // Find all hour row elements
      const hourRows = scheduleGridRef.current.querySelectorAll('[data-hour]');
      if (!hourRows.length) return;
      
      // Get the schedule grid's top position (accounting for sticky headers)
      const gridRect = scheduleGridRef.current.getBoundingClientRect();
      const headerOffset = 120; // Approximate height of sticky headers
      
      // Find the first hour row that's visible below the header
      let topVisibleHour = null;
      for (const row of hourRows) {
        const rect = row.getBoundingClientRect();
        if (rect.top >= headerOffset - 30) { // Allow some tolerance
          topVisibleHour = parseInt(row.getAttribute('data-hour'), 10);
          break;
        }
      }
      
      // If scrolled past all rows, use the last hour
      if (topVisibleHour === null && hourRows.length > 0) {
        const lastRow = hourRows[hourRows.length - 1];
        topVisibleHour = parseInt(lastRow.getAttribute('data-hour'), 10);
      }
      
      setVisibleTopHour(topVisibleHour);
    };
    
    // Attach scroll listener to window (since schedule scrolls with page)
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Initial check
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const loadTimeline = async () => {
    try {
      const res = await getTimelineApi(selectedDate);
      setTimelineData(res.data);
      // Also load global alerts for cross-day awareness
      try {
        const alertsRes = await getGlobalAlertsApi(selectedDate);
        setGlobalAlerts(alertsRes.data);
      } catch (alertErr) {
        console.error('Failed to load global alerts:', alertErr);
      }
    } catch (err) {
      console.error('Failed to load timeline:', err);
    }
    setLoading(false);
  };

  // Cross-day navigation: Handle pending anchor after data loads (avoids setTimeout race condition)
  useEffect(() => {
    if (!timelineData || loading) return;
    
    // Handle pending anchor (scroll-to-highlight)
    if (pendingAnchorRef.current) {
      const bookingId = pendingAnchorRef.current;
      pendingAnchorRef.current = null;
      
      // Small delay to ensure DOM has rendered
      requestAnimationFrame(() => {
        setAnchoredBookingId(bookingId);
        const element = document.querySelector(`[data-booking-id="${bookingId}"]`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        // Clear highlight after 2 seconds
        setTimeout(() => setAnchoredBookingId(null), 2000);
      });
    }
    
    // Handle pending drawer booking (open details after navigation)
    if (pendingDrawerBookingRef.current) {
      const targetBookingId = pendingDrawerBookingRef.current;
      pendingDrawerBookingRef.current = null;
      
      const freshBooking = (timelineData.bookings || []).find(b => b.id === targetBookingId);
      if (freshBooking) {
        const courtObj = (timelineData.courts || []).find(c => c.id === freshBooking.court_id);
        setSelectedCell({
          courtId: freshBooking.court_id,
          courtName: courtObj?.name || freshBooking.court_name,
          hour: freshBooking.start_hour,
          booking: freshBooking,
          openPlay: null,
          block: null
        });
        setDrawerOpen(true);
      }
    }
  }, [timelineData, loading]);

  // Fetch coaching sessions when dialog opens
  useEffect(() => {
    if (coachingSessionOpen) {
      getCoachingSessionsApi()
        .then(res => setCoachingSessions(res.data || []))
        .catch(err => console.error('Failed to fetch coaching sessions:', err));
    }
  }, [coachingSessionOpen]);

  // Extract data from timelineData - these are needed by hooks before early return
  const courts = timelineData?.courts || [];
  const bookings = timelineData?.bookings || [];
  const openPlaySessions = timelineData?.open_play_sessions || [];
  const courtBlocks = timelineData?.court_blocks || [];
  const rules = timelineData?.rules || {};
  const startH = rules.operating_start_hour || 6;
  const endH = rules.operating_end_hour || 22;
  const currentHour = getCurrentHourPHT();
  const isToday = selectedDate === getTodayPHT();

  // Pre-compute cell lookup maps for O(1) access (performance optimization)
  // Must be defined before early return to maintain hook order
  const { bookingMap, openPlayMap, blockMap } = useMemo(() => {
    // Build booking map: key = "courtId-hour", value = booking
    const bMap = new Map();
    bookings.forEach(b => {
      for (let h = b.start_hour; h < b.end_hour; h++) {
        bMap.set(`${b.court_id}-${h}`, b);
      }
    });
    
    // Build open play map: key = "courtId-hour", value = openPlay session
    const opMap = new Map();
    openPlaySessions.forEach(op => {
      (op.court_ids || []).forEach(courtId => {
        // end_hour is EXCLUSIVE
        for (let h = op.start_hour; h < op.end_hour; h++) {
          opMap.set(`${courtId}-${h}`, op);
        }
      });
    });
    
    // Build block map: key = "courtId-hour", value = block
    const blMap = new Map();
    courtBlocks.forEach(bl => {
      // end_hour is EXCLUSIVE — block 5PM-8PM means hours 17,18,19 blocked (not 20)
      for (let h = bl.start_hour; h < bl.end_hour; h++) {
        blMap.set(`${bl.court_id}-${h}`, bl);
      }
    });
    
    return { bookingMap: bMap, openPlayMap: opMap, blockMap: blMap };
  }, [bookings, openPlaySessions, courtBlocks]);

  // Next Event Predictor - find nearest upcoming session (memoized for performance)
  // Must be defined before early return to maintain hook order
  const nextEvent = useMemo(() => {
    if (!isToday || !timelineData) return null;
    const now = new Date();
    const currentMinutes = currentHour * 60 + now.getMinutes();
    
    // Context Locking: Use visible hour if scrolled, otherwise use current time
    const contextHour = visibleTopHour !== null ? visibleTopHour : currentHour;
    const contextMinutes = visibleTopHour !== null ? visibleTopHour * 60 : currentMinutes;
    
    // Collect all events with their start times
    const events = [];
    
    // Add bookings
    bookings.forEach(b => {
      const startMinutes = b.start_hour * 60;
      if (startMinutes >= contextMinutes - 30) {
        events.push({
          type: 'booking',
          startHour: b.start_hour,
          startMinutes,
          courtName: b.court_name,
          courtId: b.court_id,
          playerName: b.player_name,
          status: b.status,
          booking: b
        });
      }
    });
    
    // Add open play sessions
    openPlaySessions.forEach(op => {
      const startMinutes = op.start_hour * 60;
      if (startMinutes >= contextMinutes - 30) {
        const courtName = courts.find(c => (op.court_ids || []).includes(c.id))?.name || 'Courts';
        events.push({
          type: 'openPlay',
          startHour: op.start_hour,
          startMinutes,
          courtName,
          playerName: 'Open Play',
          status: 'open_play',
          openPlay: op
        });
      }
    });
    
    // Check for empty slots coming up (within context)
    courts.forEach(court => {
      [contextHour, contextHour + 1].forEach(hour => {
        if (hour < startH || hour >= endH) return;
        const hasBooking = bookings.some(b => 
          b.court_id === court.id && b.start_hour <= hour && b.end_hour > hour
        );
        const hasOpenPlay = openPlaySessions.some(op => 
          (op.court_ids || []).includes(court.id) && op.start_hour <= hour && op.end_hour >= hour
        );
        const hasBlock = courtBlocks.some(bl => 
          bl.court_id === court.id && bl.start_hour <= hour && bl.end_hour > hour
        );
        if (!hasBooking && !hasOpenPlay && !hasBlock) {
          const startMinutes = hour * 60;
          if (startMinutes >= contextMinutes) {
            events.push({
              type: 'empty',
              startHour: hour,
              startMinutes,
              courtName: court.name,
              courtId: court.id,
              playerName: null,
              status: 'empty'
            });
          }
        }
      });
    });
    
    // Sort by start time and get the nearest upcoming from context
    events.sort((a, b) => a.startMinutes - b.startMinutes);
    
    // Find the next event from the context point
    let foundEvent = events.find(e => e.startMinutes >= contextMinutes - 30);
    
    // Fallback: If no events in visible context, show the closest upcoming event
    if (!foundEvent && visibleTopHour !== null) {
      if (events.length > 0) {
        foundEvent = events[0];
      } else {
        const firstAvailableCourt = courts[0];
        if (firstAvailableCourt && visibleTopHour >= startH && visibleTopHour < endH) {
          foundEvent = {
            type: 'empty',
            startHour: visibleTopHour,
            startMinutes: visibleTopHour * 60,
            courtName: firstAvailableCourt.name,
            courtId: firstAvailableCourt.id,
            playerName: null,
            status: 'empty'
          };
        }
      }
    }
    
    if (!foundEvent) return null;
    
    // Determine display status
    const minutesUntilStart = foundEvent.startMinutes - currentMinutes;
    let displayStatus = 'confirmed';
    let statusLabel = 'CONFIRMED';
    let statusColor = 'text-brand-teal';
    
    if (foundEvent.status === 'empty') {
      displayStatus = 'empty';
      statusLabel = 'AVAILABLE';
      statusColor = 'text-gray-500';
    } else if (foundEvent.status === 'open_play') {
      displayStatus = 'openPlay';
      statusLabel = 'OPEN PLAY';
      statusColor = 'text-open-play';
    } else if (foundEvent.status === 'pending_payment') {
      if (minutesUntilStart <= 0) {
        displayStatus = 'overdue';
        statusLabel = 'OVERDUE';
        statusColor = 'text-status-expired';
      } else if (minutesUntilStart <= 10) {
        displayStatus = 'startingSoon';
        statusLabel = 'STARTING SOON';
        statusColor = 'text-status-held';
      } else {
        displayStatus = 'awaitingPayment';
        statusLabel = 'AWAITING PAYMENT';
        statusColor = 'text-status-held';
      }
    } else if (foundEvent.status === 'pending_approval') {
      displayStatus = 'pendingApproval';
      statusLabel = 'NEEDS APPROVAL';
      statusColor = 'text-status-review';
    } else if (foundEvent.status === 'confirmed') {
      if (foundEvent.payment_status === 'partial') {
        displayStatus = 'partialPayment';
        statusLabel = 'BALANCE DUE';
        statusColor = 'text-amber-600';
      } else {
        displayStatus = 'confirmed';
        statusLabel = 'CONFIRMED';
        statusColor = 'text-brand-teal';
      }
    }
    
    return {
      ...foundEvent,
      displayStatus,
      statusLabel,
      statusColor,
      minutesUntilStart,
      isContextLocked: visibleTopHour !== null && visibleTopHour !== currentHour
    };
  }, [isToday, timelineData, currentHour, visibleTopHour, bookings, openPlaySessions, courtBlocks, courts, startH, endH]);

  // Show nothing while auth is checking
  if (authLoading || !user) return null;

  const hours = Array.from({ length: endH - startH }, (_, i) => startH + i);

  // Compute "Needs Action" items
  const needsActionItems = bookings.filter(b => {
    // pending_payment status
    if (b.status === 'pending_payment') return true;
    // pending_approval status (proof uploaded, awaiting verification)
    if (b.status === 'pending_approval') return true;
    // expiring_soon: pending_payment with hold_expires_at within 10 minutes
    if (b.status === 'pending_payment' && b.hold_expires_at) {
      const expiresAt = new Date(b.hold_expires_at);
      const now = new Date();
      const diff = expiresAt - now;
      if (diff > 0 && diff <= 10 * 60 * 1000) return true; // within 10 minutes
    }
    return false;
  });
  
  const needsActionCounts = {
    pendingPayment: bookings.filter(b => b.status === 'pending_payment').length,
    pendingApproval: bookings.filter(b => b.status === 'pending_approval').length,
    expiringSoon: bookings.filter(b => {
      if (b.status !== 'pending_payment' || !b.hold_expires_at) return false;
      const expiresAt = new Date(b.hold_expires_at);
      const now = new Date();
      const diff = expiresAt - now;
      return diff > 0 && diff <= 10 * 60 * 1000;
    }).length,
    total: 0
  };
  needsActionCounts.total = needsActionCounts.pendingPayment + needsActionCounts.pendingApproval;

  // Ops Status Bar computed values
  const opsStatusCounts = {
    needApproval: bookings.filter(b => b.status === 'pending_approval').length,
    awaitingPayment: bookings.filter(b => b.status === 'pending_payment').length,
    confirmedToday: isToday ? bookings.filter(b => b.status === 'confirmed').length : 0,
    emptySoon: (() => {
      if (!isToday) return 0;
      const now = new Date();
      const currentMinute = now.getMinutes();
      // Check slots starting within 60 minutes
      const slotsToCheck = [];
      if (currentMinute < 60) slotsToCheck.push(currentHour);
      slotsToCheck.push(currentHour + 1);
      
      let emptyCount = 0;
      courts.forEach(court => {
        slotsToCheck.forEach(hour => {
          if (hour < startH || hour >= endH) return;
          const hasBooking = bookings.some(b => 
            b.court_id === court.id && b.start_hour <= hour && b.end_hour > hour
          );
          const hasOpenPlay = openPlaySessions.some(op => 
            (op.court_ids || []).includes(court.id) && op.start_hour <= hour && op.end_hour >= hour
          );
          const hasBlock = courtBlocks.some(bl => 
            bl.court_id === court.id && bl.start_hour <= hour && bl.end_hour > hour
          );
          if (!hasBooking && !hasOpenPlay && !hasBlock) {
            emptyCount++;
          }
        });
      });
      return emptyCount;
    })()
  };

  // Urgency states for Ops Status Bar
  const urgencyStates = {
    // Any booking starting within 10 minutes and still pending payment
    hasStartingSoon: isToday && bookings.some(b => {
      if (b.status !== 'pending_payment') return false;
      const now = new Date();
      const currentMinutes = currentHour * 60 + now.getMinutes();
      const bookingStartMinutes = b.start_hour * 60;
      const minutesUntilStart = bookingStartMinutes - currentMinutes;
      return minutesUntilStart > 0 && minutesUntilStart <= 10;
    }),
    // Any booking that has passed start time and is still pending payment (overdue)
    hasOverdue: isToday && bookings.some(b => {
      if (b.status !== 'pending_payment') return false;
      const now = new Date();
      const currentMinutes = currentHour * 60 + now.getMinutes();
      const bookingStartMinutes = b.start_hour * 60;
      return currentMinutes >= bookingStartMinutes;
    })
  };

  // Blocking Issues: Bookings that block courts but are not payable anymore
  // These are bookings where:
  // - Hold has expired (hold_expires_at passed)
  // - Status is still pending_payment or released but blocking a slot
  // - No payment proof uploaded
  const blockingIssues = bookings.filter(b => {
    // Check if booking is blocking a court (not confirmed, not cancelled)
    if (b.status === 'confirmed' || b.status === 'cancelled') return false;
    
    // Released bookings have their court_slots removed by the backend
    // They cannot block courts, so never flag them as blocking issues
    if (b.status === 'released') return false;
    
    // Check if hold has expired with no payment proof - this is a blocking issue
    if (b.hold_expires_at && !b.payment_proof) {
      const expiresAt = new Date(b.hold_expires_at);
      const now = new Date();
      if (now > expiresAt) {
        return true;
      }
    }
    
    return false;
  });
  
  const hasBlockingIssues = blockingIssues.length > 0;

  // Header Mode: CALM vs ATTENTION
  // CALM: No urgent items AND no blocking issues - quiet header
  // ATTENTION: Any urgent item or blocking issue exists - emphasized header
  const headerMode = (() => {
    // Blocking issues are highest priority
    if (hasBlockingIssues) {
      return 'attention';
    }
    
    // Check for any urgent conditions
    const hasNeedApproval = opsStatusCounts.needApproval > 0;
    const hasOverdue = urgencyStates.hasOverdue;
    const hasStartingSoon = urgencyStates.hasStartingSoon;
    
    // Check for awaiting payment within 30 minutes
    const hasAwaitingPaymentSoon = isToday && bookings.some(b => {
      if (b.status !== 'pending_payment') return false;
      const now = new Date();
      const currentMinutes = currentHour * 60 + now.getMinutes();
      const bookingStartMinutes = b.start_hour * 60;
      const minutesUntilStart = bookingStartMinutes - currentMinutes;
      return minutesUntilStart > 0 && minutesUntilStart <= 30;
    });
    
    // ATTENTION MODE triggers
    if (hasNeedApproval || hasOverdue || hasStartingSoon) {
      return 'attention';
    }
    
    // If awaiting payment within 30 min, still show some emphasis
    if (hasAwaitingPaymentSoon) {
      return 'attention';
    }
    
    // CALM MODE - no urgent items and no blocking issues
    return 'calm';
  })();

  // Filter bookings based on opsFilter
  const getFilteredBookingIds = () => {
    if (!opsFilter) return null; // No filter, show all
    
    switch (opsFilter) {
      case 'actionRequired':
        return new Set(blockingIssues.map(b => b.id));
      case 'needApproval':
        return new Set(bookings.filter(b => b.status === 'pending_approval').map(b => b.id));
      case 'awaitingPayment':
        return new Set(bookings.filter(b => b.status === 'pending_payment').map(b => b.id));
      case 'confirmedToday':
        return new Set(bookings.filter(b => b.status === 'confirmed').map(b => b.id));
      case 'emptySoon':
        return 'emptySoon'; // Special case handled in grid
      default:
        return null;
    }
  };
  
  const filteredBookingIds = getFilteredBookingIds();

  // Optimized cell data lookup using pre-computed maps (O(1) instead of O(n))
  const getCellData = (courtId, hour) => {
    const key = `${courtId}-${hour}`;
    const booking = bookingMap.get(key) || null;
    const openPlay = openPlayMap.get(key) || null;
    const block = blockMap.get(key) || null;
    return { booking, openPlay, block };
  };

  const handleCellClick = (courtId, hour) => {
    const courtObj = courts.find(c => c.id === courtId);
    const { booking, openPlay, block } = getCellData(courtId, hour);
    setSelectedCell({ courtId, courtName: courtObj?.name, hour, booking, openPlay, block });
    setOpInlineEditMode(false);
    setReleaseOpenPlayConfirmOpen(false);
    setDrawerOpen(true);
    // Load tournament details if this is a tournament block
    if (block?.tournament_id) {
      loadTournamentForBlock(block);
    } else {
      setEditTournament(null);
    }
  };

  // Shared pricing helper — computes the base total the booking would cost
  // BEFORE any special-rate override. Used by both the modal preview and handleQuickBook.
  const computeQbBreakdown = () => {
    const r = rules || {};
    const start = selectedCell?.hour ?? parseInt(qbStartHour);
    const dur = parseInt(qbDuration) || 1;
    const hourly = r.hourly_rate ?? 350;
    const dayRate = r.day_rate ?? 350;
    const eveningRate = r.evening_rate ?? 350;
    const eveningStart = r.evening_start_hour ?? 16;
    const trainingEveningStart = r.training_evening_start_hour ?? 18;
    const trainingDayRate = r.training_day_rate ?? 250;
    const coachingRate = r.coaching_rate ?? 250;
    const coachingPromoStart = r.coaching_promo_start_hour ?? 6;
    const coachingPromoEnd = r.coaching_promo_end_hour ?? 18;
    const equipRate = qbIsMember ? (r.member_training_rate ?? 400) : (r.equipment_rate_player ?? 450);
    const tpFlat = r.training_tp_share_per_booking ?? 100;

    if (qbPaymentStatus === 'free') {
      return { rateLine: 'Free · ₱0/hr', total: 0, firstHourRate: 0, regularRate: 0 };
    }

    let total = 0;
    let firstHourRate = 0;
    let regularRate = 0;
    if (isNaN(start)) {
      // fallback estimate
      if (qbBookingType === 'training') {
        total = trainingDayRate * dur + (qbIsSpecialPrivilege ? tpFlat : equipRate * dur);
        firstHourRate = trainingDayRate + (qbIsSpecialPrivilege ? tpFlat / dur : equipRate);
        regularRate = firstHourRate;
      } else if (qbBookingType === 'coaching') {
        total = coachingRate * dur; firstHourRate = coachingRate; regularRate = hourly;
      } else {
        total = dayRate * dur; firstHourRate = dayRate; regularRate = dayRate;
      }
      return {
        rateLine: `₱${firstHourRate.toLocaleString()}/hr`,
        total,
        firstHourRate,
        regularRate,
      };
    }

    if (qbBookingType === 'training') {
      let courtCost = 0;
      for (let h = start; h < start + dur; h++) {
        courtCost += (h < trainingEveningStart) ? trainingDayRate : eveningRate;
      }
      const equipCost = qbIsSpecialPrivilege ? tpFlat : (equipRate * dur);
      total = courtCost + equipCost;
      firstHourRate = (start < trainingEveningStart ? trainingDayRate : eveningRate) + (qbIsSpecialPrivilege ? tpFlat / dur : equipRate);
      regularRate = firstHourRate; // for training the "regular" side isn't meaningful — same
      return {
        rateLine: qbIsSpecialPrivilege
          ? `Court ₱${courtCost.toLocaleString()} + ₱${tpFlat} TP share`
          : `Court ₱${courtCost.toLocaleString()} + Package ₱${equipCost.toLocaleString()}`,
        total,
        firstHourRate,
        regularRate,
      };
    }
    if (qbBookingType === 'coaching') {
      for (let h = start; h < start + dur; h++) {
        total += (h >= coachingPromoStart && h < coachingPromoEnd) ? coachingRate : hourly;
      }
      const firstIsPromo = start >= coachingPromoStart && start < coachingPromoEnd;
      firstHourRate = firstIsPromo ? coachingRate : hourly;
      regularRate = hourly;
      return {
        rateLine: firstIsPromo
          ? `₱${coachingRate}/hr coaching (6 AM – 6 PM)`
          : `₱${hourly}/hr coaching (regular hours)`,
        total,
        firstHourRate,
        regularRate,
      };
    }
    // Court Rental (regular)
    for (let h = start; h < start + dur; h++) {
      total += (h < eveningStart) ? dayRate : eveningRate;
    }
    const startIsDay = start < eveningStart;
    firstHourRate = startIsDay ? dayRate : eveningRate;
    regularRate = firstHourRate;
    return {
      rateLine: `₱${firstHourRate}/hr ${startIsDay ? 'day rate' : 'evening rate'}`,
      total,
      firstHourRate,
      regularRate,
    };
  };
  const computeQbBaseTotal = () => computeQbBreakdown().total;

  const handleQuickBook = async () => {
    const courtId = selectedCell?.courtId || qbCourtId;
    const startHour = selectedCell?.hour ?? parseInt(qbStartHour);
    if (!qbName.trim() || !courtId || isNaN(startHour)) {
      toast.error('Fill in all required fields');
      return;
    }
    // Translate "Training" pseudo payment type → backend payload
    // New booking-type / payment-status model
    const isTraining = qbBookingType === 'training';
    const isCoaching = qbBookingType === 'coaching';
    // Map booking type to backend payment_type: coaching/training trigger their rate compute paths
    const bookingPaymentType =
      qbPaymentStatus === 'free' ? 'free'
      : isTraining ? 'training'
      : isCoaching ? 'coaching'
      : (qbPaymentStatus === 'unpaid' ? 'pending' : 'paid');
    // Force final payment_status when using coaching/training (backend derives 'paid' by default)
    const paymentStatusOverride =
      qbPaymentStatus === 'free' ? null
      : (isTraining || isCoaching) ? (qbPaymentStatus === 'unpaid' ? 'pending' : 'paid') : null;
    // Special rate override → converted to a discount so backend math stays simple
    const specialRateRaw = parseFloat(qbSpecialRateAmount);
    const durationInt = parseInt(qbDuration);
    let discountAmt = 0;
    if (qbSpecialRateOn && !isNaN(specialRateRaw) && specialRateRaw >= 0) {
      const base = computeQbBaseTotal();
      const overrideTotal = specialRateRaw * durationInt;
      discountAmt = Math.max(0, base - overrideTotal);
    }
    const payload = {
      court_id: courtId,
      date: selectedDate,
      start_hour: startHour,
      duration_hours: durationInt,
      player_name: qbName.trim(),
      player_phone: qbPhone.trim() || null,
      auto_confirm: true,
      payment_type: bookingPaymentType,
      has_equipment: isTraining,
      equipment_use_standard_rate: isTraining,
      booking_intent: isTraining ? 'ball_machine' : 'court',
      is_member: isTraining ? qbIsMember : false,
      is_special_privilege: isTraining ? qbIsSpecialPrivilege : false,
      discount_amount: discountAmt,
      payment_status_override: paymentStatusOverride,
    };
    try {
      const res = await createBookingApi(payload);
      const isUnpaid = qbPaymentStatus === 'unpaid';
      toast.success(
        `Booking ${isUnpaid ? 'confirmed · unpaid' : (qbPaymentStatus === 'free' ? 'confirmed · free' : 'confirmed & paid')} · Total ₱${(res.data?.amount || 0).toLocaleString()}`
      );
      setCreateBookingOpen(false);
      setDrawerOpen(false);
      setQbName('');
      setQbPhone('');
      setQbCourtId('');
      setQbStartHour('');
      setQbBookingType('court');
      setQbPaymentStatus('paid');
      setQbIsMember(false);
      setQbIsSpecialPrivilege(false);
      setQbSpecialRateOn(false);
      setQbSpecialRateAmount('');
      loadTimeline();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : detail?.message || 'Failed to create booking');
    }
  };

  const handleReleaseBooking = async (bookingId) => {
    try {
      await releaseBookingApi(bookingId);
      toast.success('Booking released');
      setDrawerOpen(false);
      loadTimeline();
    } catch (err) {
      toast.error('Failed to release booking');
    }
  };

  const handleMarkPaid = async (bookingId) => {
    try {
      await markPaidApi(bookingId);
      toast.success('Marked as paid');
      loadTimeline();
      setDrawerOpen(false);
    } catch (err) {
      toast.error('Failed to mark paid');
    }
  };

  const handleCreateOpenPlay = async () => {
    if (opCourts.length === 0 || !opStartHour || !opEndHour) {
      toast.error('Fill in all open play details');
      return;
    }
    try {
      await createOpenPlayApi({
        court_ids: opCourts,
        date: opDate,
        start_hour: parseInt(opStartHour),
        end_hour: parseInt(opEndHour),
        max_players: parseInt(opMaxPlayers),
        price_per_player: parseFloat(opPrice),
        allow_walkins: opWalkins,
        auto_close_when_full: opAutoClose,
      });
      toast.success('Open Play session created');
      setCreateOpenPlayOpen(false);
      // Refresh timeline if viewing the same date
      if (opDate === selectedDate) {
        loadTimeline();
      }
    } catch (err) {
      toast.error('Failed to create open play');
    }
  };

  const handleAddPlayerToOpenPlay = async (sessionId) => {
    if (!addPlayerName.trim()) return;
    try {
      await joinOpenPlayApi(sessionId, { player_name: addPlayerName.trim() });
      toast.success('Player added');
      setAddPlayerName('');
      await refreshAndUpdateOpenPlayDrawer(sessionId);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add player');
    }
  };

  const handleRemovePlayer = async (sessionId, playerId) => {
    try {
      await removeFromOpenPlayApi(sessionId, playerId);
      toast.success('Player removed');
      await refreshAndUpdateOpenPlayDrawer(sessionId);
    } catch (err) {
      toast.error('Failed to remove player');
    }
  };

  const handleCheckinPlayer = async (sessionId, playerId) => {
    try {
      await checkinPlayerApi(sessionId, playerId);
      toast.success('Checked in');
      await refreshAndUpdateOpenPlayDrawer(sessionId);
    } catch (err) {
      toast.error('Failed to check in');
    }
  };

  // Execute the actual open play release (called after inline confirmation)
  const executeReleaseOpenPlay = async (sessionId) => {
    try {
      await cancelOpenPlayApi(sessionId);
      toast.success('Open Play session released');
      setSelectedCell(null);
      setReleaseOpenPlayConfirmOpen(false);
      loadTimeline();
    } catch (err) {
      toast.error('Failed to release session');
    }
  };

  const handleUpdateOpenPlay = async () => {
    if (!editOpenPlaySession) return;
    try {
      await updateOpenPlayApi(editOpenPlaySession.id, {
        start_hour: parseInt(editOpStartHour),
        end_hour: parseInt(editOpEndHour),
        max_players: parseInt(editOpMaxPlayers),
        price_per_player: parseFloat(editOpPrice),
      });
      toast.success('Open Play updated');
      setEditOpenPlayOpen(false);
      setSelectedCell(null);
      loadTimeline();
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  // Inline update for Open Play (used from drawer)
  const handleUpdateOpenPlayInline = async () => {
    if (!selectedCell?.openPlay) return;
    setActionLoading(true);
    try {
      await updateOpenPlayApi(selectedCell.openPlay.id, {
        start_hour: parseInt(editOpStartHour),
        end_hour: parseInt(editOpEndHour),
        max_players: parseInt(editOpMaxPlayers),
        price_per_player: parseFloat(editOpPrice),
      });
      toast.success('Open Play updated');
      setOpInlineEditMode(false);
      await refreshAndUpdateOpenPlayDrawer(selectedCell.openPlay.id);
    } catch (err) {
      toast.error('Failed to update');
    }
    setActionLoading(false);
  };

  const handleBlockCourt = async () => {
    const courtId = selectedCell?.courtId || blockCourtId;
    const startHour = selectedCell?.hour ?? parseInt(blockStartHour);
    if (!courtId || isNaN(startHour) || !blockEndHour) {
      toast.error('Fill in all required fields');
      return;
    }
    
    // Handle "All Courts" option
    const courtsToBlock = courtId === 'all' ? courts.map(c => c.id) : [courtId];
    
    try {
      // Create blocks for each court
      for (const cId of courtsToBlock) {
        await createCourtBlockApi({
          court_id: cId,
          date: selectedDate,
          start_hour: startHour,
          end_hour: parseInt(blockEndHour),
          reason: blockReason.trim(),
        });
      }
      toast.success(courtsToBlock.length > 1 ? 'All courts blocked' : 'Court blocked');
      setBlockCourtOpen(false);
      setDrawerOpen(false);
      setBlockReason('');
      setBlockEndHour('');
      setBlockCourtId('');
      setBlockStartHour('');
      loadTimeline();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to block court');
    }
  };

  const handleRemoveBlock = async (blockId) => {
    try {
      await removeCourtBlockApi(blockId);
      toast.success('Block removed');
      setDrawerOpen(false);
      loadTimeline();
    } catch (err) {
      toast.error('Failed to remove block');
    }
  };

  const handlePayPlayer = async (sessionId, playerId) => {
    try {
      await payOpenPlayApi(sessionId, playerId);
      toast.success('Payment recorded');
      await refreshAndUpdateOpenPlayDrawer(sessionId);
    } catch (err) {
      toast.error('Failed to record payment');
    }
  };

  // Live-update helper: refresh timeline and update drawer with fresh booking data
  const refreshAndUpdateDrawer = async (bookingId) => {
    try {
      const res = await getTimelineApi(selectedDate);
      setTimelineData(res.data);
      if (bookingId) {
        const freshBooking = (res.data.bookings || []).find(b => b.id === bookingId);
        if (freshBooking) {
          setSelectedCell(prev => {
            if (!prev) return prev;
            return { ...prev, booking: freshBooking };
          });
        }
      }
    } catch (err) {
      console.error('Failed to refresh drawer:', err);
    }
  };

  // Refresh timeline and update Open Play data in drawer
  const refreshAndUpdateOpenPlayDrawer = async (openPlayId) => {
    const res = await getTimelineApi(selectedDate);
    setTimelineData(res.data);
    if (selectedCell && openPlayId) {
      const freshOpenPlay = (res.data.open_play_sessions || []).find(op => op.id === openPlayId);
      if (freshOpenPlay) {
        setSelectedCell(prev => ({ ...prev, openPlay: freshOpenPlay }));
      }
    }
  };

  const handleExtendBooking = async (bookingId, extraHours) => {
    setActionLoading(true);
    try {
      const currentBooking = selectedCell?.booking;
      const newEndHour = currentBooking ? currentBooking.end_hour + extraHours : null;
      await extendBookingApi(bookingId, extraHours);
      // Show absolute time feedback
      const endTimeStr = newEndHour ? formatHour(newEndHour) : '';
      toast.success(`Extended to ${endTimeStr}`);
      await refreshAndUpdateDrawer(bookingId);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Cannot extend — court occupied');
    }
    setActionLoading(false);
  };

  const handleMoveBooking = async (bookingId, newCourtId) => {
    setActionLoading(true);
    try {
      await moveBookingApi(bookingId, newCourtId);
      toast.success('Moved to new court');
      await refreshAndUpdateDrawer(bookingId);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Cannot move — court occupied');
    }
    setActionLoading(false);
  };

  const handleSaveNote = async (bookingId) => {
    setActionLoading(true);
    try {
      await updateBookingNotesApi(bookingId, noteText);
      toast.success('Note saved');
      await refreshAndUpdateDrawer(bookingId);
    } catch (err) {
      toast.error('Failed to save note');
    }
    setActionLoading(false);
  };

  const handleCreateRecurringBooking = async () => {
    if (!rbPlayerName.trim()) { toast.error('Enter player name'); return; }
    if (!rbPlayerEmail.trim()) { toast.error('Enter player email'); return; }
    if (!rbCourtId) { toast.error('Select a court'); return; }
    if (!rbStartHour) { toast.error('Select start time'); return; }
    if (rbWeekdays.length === 0) { toast.error('Select at least one day'); return; }
    setRbSubmitting(true);
    try {
      const res = await createRecurringBookingApi({
        player_name: rbPlayerName.trim(),
        player_email: rbPlayerEmail.trim(),
        player_phone: rbPlayerPhone.trim() || null,
        court_id: rbCourtId,
        start_hour: parseInt(rbStartHour),
        duration_hours: parseInt(rbDuration),
        weekdays: rbWeekdays.sort(),
        start_date: rbStartDate,
        repeat_interval: parseInt(rbInterval),
      });
      toast.success(`Created! ${res.data.instances_created} bookings scheduled`);
      setRecurringBookingOpen(false);
      setRbPlayerName(''); setRbPlayerEmail(''); setRbPlayerPhone('');
      setRbCourtId(''); setRbStartHour(''); setRbDuration('1');
      setRbWeekdays([]); setRbStartDate(getTodayPHT());
      setRbInterval('1');
      loadTimeline();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create recurring booking');
    }
    setRbSubmitting(false);
  };

  const handleCreateTournament = async () => {
    if (!tName.trim()) { toast.error('Enter tournament name'); return; }
    if (!tOrganizerName.trim()) { toast.error('Enter organizer name'); return; }
    if (tCourts.length === 0) { toast.error('Select at least one court'); return; }
    if (!tStartDate || !tEndDate) { toast.error('Select date range'); return; }
    if (tStartDate > tEndDate) { toast.error('End date must be after start date'); return; }
    
    setTSubmitting(true);
    try {
      const res = await createTournamentApi({
        name: tName.trim(),
        organizer_name: tOrganizerName.trim(),
        organizer_phone: tOrganizerPhone.trim() || null,
        organizer_email: tOrganizerEmail.trim() || null,
        court_ids: tCourts,
        start_date: tStartDate,
        end_date: tEndDate,
        start_hour: parseInt(tStartHour),
        end_hour: parseInt(tEndHour),
        additional_charges: tAdditionalCharges.filter(c => c.name && c.amount > 0),
        notes: tNotes.trim() || null,
      });
      toast.success(`Tournament "${res.data.name}" created! Courts blocked for ${res.data.num_days} day(s).`);
      setTournamentOpen(false);
      setTName(''); setTOrganizerName(''); setTOrganizerPhone(''); setTOrganizerEmail('');
      setTCourts([]); setTStartDate(getTodayPHT()); setTEndDate(getTodayPHT());
      setTStartHour('6'); setTEndHour('18'); setTAdditionalCharges([]); setTNotes('');
      loadTimeline();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create tournament');
    }
    setTSubmitting(false);
  };

  // Load tournament details when a tournament block is selected
  const loadTournamentForBlock = async (block) => {
    if (!block?.tournament_id) { setEditTournament(null); return; }
    try {
      const res = await getTournamentsApi();
      const t = res.data.find(t => t.id === block.tournament_id);
      if (t) {
        setEditTournament(t);
        setEditTCharges(t.additional_charges?.length > 0 ? [...t.additional_charges] : []);
      }
    } catch { setEditTournament(null); }
  };

  const handleSaveTournamentCharges = async () => {
    if (!editTournament) return;
    setEditTSaving(true);
    try {
      await updateTournamentApi(editTournament.id, {
        additional_charges: editTCharges.filter(c => c.name && parseFloat(c.amount) > 0)
      });
      toast.success('Tournament charges updated');
      loadTournamentForBlock({ tournament_id: editTournament.id });
    } catch (err) {
      toast.error('Failed to update charges');
    }
    setEditTSaving(false);
  };

  const handleCancelTournament = async (tid) => {
    if (!window.confirm('Cancel this tournament and release all courts?')) return;
    try {
      await cancelTournamentApi(tid);
      toast.success('Tournament cancelled');
      setDrawerOpen(false);
      setEditTournament(null);
      loadTimeline();
    } catch (err) {
      toast.error('Failed to cancel tournament');
    }
  };


  const toggleFilter = (key) => {
    setFilters(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleRbWeekday = (day) => {
    setRbWeekdays(prev => prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]);
  };

  const WEEKDAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  // Check if a court is available for moving a booking to it
  const isCourtAvailableForMove = (courtId, booking) => {
    if (!booking) return false;
    const startHour = booking.start_hour;
    const endHour = booking.end_hour;
    // Check for overlapping bookings
    const hasBookingConflict = bookings.some(b =>
      b.court_id === courtId &&
      b.id !== booking.id &&
      b.start_hour < endHour &&
      b.end_hour > startHour
    );
    // Check for overlapping open play
    const hasOpenPlayConflict = openPlaySessions.some(op =>
      (op.court_ids || []).includes(courtId) &&
      op.start_hour < endHour &&
      op.end_hour >= startHour
    );
    // Check for overlapping blocks (end_hour is exclusive)
    const hasBlockConflict = courtBlocks.some(bl =>
      bl.court_id === courtId &&
      bl.start_hour < endHour &&
      bl.end_hour > startHour
    );
    return !hasBookingConflict && !hasOpenPlayConflict && !hasBlockConflict;
  };

  // Check if extending by X hours would conflict
  const canExtendByHours = (booking, extraHours) => {
    if (!booking) return false;
    const newEndHour = booking.end_hour + extraHours;
    const courtId = booking.court_id;
    // Check for overlapping bookings after extension
    const hasBookingConflict = bookings.some(b =>
      b.court_id === courtId &&
      b.id !== booking.id &&
      b.start_hour < newEndHour &&
      b.end_hour > booking.end_hour
    );
    // Check for overlapping open play
    const hasOpenPlayConflict = openPlaySessions.some(op =>
      (op.court_ids || []).includes(courtId) &&
      op.start_hour < newEndHour &&
      op.end_hour >= booking.end_hour
    );
    // Check for overlapping blocks (end_hour is exclusive)
    const hasBlockConflict = courtBlocks.some(bl =>
      bl.court_id === courtId &&
      bl.start_hour < newEndHour &&
      bl.end_hour > booking.end_hour
    );
    return !hasBookingConflict && !hasOpenPlayConflict && !hasBlockConflict;
  };

  // Calculate how long a court remains free after booking's time slot
  const getCourtFreeHours = (courtId, booking) => {
    if (!booking) return 0;
    const bookingEnd = booking.end_hour;
    // Find the next event on this court AFTER the booking ends
    let nextEventStart = endH; // Default to end of operating hours
    
    // Check bookings
    bookings.forEach(b => {
      if (b.court_id === courtId && b.id !== booking.id && b.start_hour >= bookingEnd && b.start_hour < nextEventStart) {
        nextEventStart = b.start_hour;
      }
    });
    // Check open play
    openPlaySessions.forEach(op => {
      if ((op.court_ids || []).includes(courtId) && op.start_hour >= bookingEnd && op.start_hour < nextEventStart) {
        nextEventStart = op.start_hour;
      }
    });
    // Check blocks
    courtBlocks.forEach(bl => {
      if (bl.court_id === courtId && bl.start_hour >= bookingEnd && bl.start_hour < nextEventStart) {
        nextEventStart = bl.start_hour;
      }
    });
    
    return Math.max(0, nextEventStart - bookingEnd);
  };

  // Get booking context relative to current time
  const getBookingContext = (booking) => {
    if (!booking || !isToday) return null;
    const startHour = booking.start_hour;
    const endHour = booking.end_hour;
    
    if (currentHour < startHour) {
      const hoursUntil = startHour - currentHour;
      return { status: 'upcoming', label: hoursUntil === 1 ? 'Starts in 1h' : `Starts in ${hoursUntil}h`, color: 'blue' };
    } else if (currentHour >= endHour) {
      return { status: 'ended', label: 'Ended', color: 'gray' };
    } else if (endHour - currentHour <= 1) {
      return { status: 'ending', label: 'Ending soon', color: 'amber' };
    } else {
      return { status: 'inprogress', label: 'In progress', color: 'green' };
    }
  };

  // Get recent unique notes from bookings for suggestions
  const getRecentNotes = () => {
    const notesSet = new Set();
    bookings.forEach(b => {
      if (b.notes && b.notes.trim()) {
        notesSet.add(b.notes.trim());
      }
    });
    return Array.from(notesSet).slice(0, 5); // Max 5 suggestions
  };

  // Check if booking spans from this hour (is the starting cell)
  const isBookingStart = (booking, hour) => booking && booking.start_hour === hour;
  const isOpenPlayStart = (openPlay, courtId, hour) => {
    if (!openPlay) return false;
    // Check if this is the start hour AND this court is part of the open play session
    return openPlay.start_hour === hour && (openPlay.court_ids || []).includes(courtId);
  };
  const isBlockStart = (block, hour) => block && block.start_hour === hour;

  return (
    <div className="min-h-screen bg-app-bg" data-testid="operator-today">
      {/* Top Bar */}
      <div className="bg-white border-b border-black/5 px-4 py-3 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h1 className="font-heading text-xl font-bold text-text-primary">Today's Schedule</h1>
            <input
              type="date"
              data-testid="timeline-date-picker"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="text-sm bg-card-bg border border-border-subtle rounded-lg px-3 py-1.5 text-text-primary"
            />
            {/* Filter Pills - Active = solid, Inactive = white with colored border */}
            <div className="hidden md:flex items-center gap-1.5 ml-2">
              <button
                data-testid="filter-bookings"
                onClick={() => toggleFilter('bookings')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all border ${
                  filters.bookings ? filterStyles.bookings.active : filterStyles.bookings.inactive
                }`}
              >
                {filters.bookings ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                Bookings
              </button>
              <button
                data-testid="filter-openPlay"
                onClick={() => toggleFilter('openPlay')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all border ${
                  filters.openPlay ? filterStyles.openPlay.active : filterStyles.openPlay.inactive
                }`}
              >
                {filters.openPlay ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                Open Play
              </button>
              <button
                data-testid="filter-blocks"
                onClick={() => toggleFilter('blocks')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all border ${
                  filters.blocks ? filterStyles.blocks.active : filterStyles.blocks.inactive
                }`}
              >
                {filters.blocks ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                Blocks
              </button>
            </div>
          </div>
          {/* Utility Bar - Secondary tools, low visual weight */}
          <div className="flex items-center gap-1.5">
            {/* Add Schedule - Outline, neutral, smaller */}
            {/* Add button - only for operators and admins */}
            {isOperator() && (
            <button
              data-testid="add-schedule-btn"
              onClick={() => {
                setPrefillCourtId(courts[0]?.id || '');
                setPrefillStartHour(String(currentHour < endH ? currentHour : startH));
                setPrefillDuration('1');
                setSchedulePickerOpen(true);
              }}
              className="flex items-center gap-1 px-2.5 py-1 rounded-lg border border-gray-300 text-gray-500 hover:text-gray-700 hover:border-gray-400 hover:bg-gray-50 text-xs font-medium transition-all"
            >
              <Plus className="w-3.5 h-3.5" /> Add
            </button>
            )}

            {/* View Mode Indicator + Navigation */}
            <div className="flex items-center bg-gray-100/70 rounded-md p-0.5">
              {/* Current view indicator - non-clickable */}
              <span
                data-testid="mode-schedule-indicator"
                className="px-2.5 py-1 text-[10px] font-medium rounded bg-white text-gray-600 shadow-sm cursor-default select-none"
              >
                Schedule View
              </span>
              {/* Admin View - only visible to admins */}
              {canAccessAdminView() && (
                <button
                  data-testid="mode-admin-view-btn"
                  onClick={() => navigate('/operator/admin')}
                  className="px-2 py-1 text-[10px] font-medium rounded text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition-colors"
                >
                  Admin View
                </button>
              )}
            </div>

            {/* Divider */}
            <div className="w-px h-5 bg-gray-200 mx-0.5" />

            {/* Icons only - settings & logout */}
            {canAccessAdminView() && (
              <>
                <button
                  data-testid="nav-manage-users-btn"
                  onClick={() => navigate('/operator/users')}
                  className="p-1.5 rounded text-gray-400 hover:text-gray-500 hover:bg-gray-100 transition-colors"
                  title="Manage Users"
                >
                  <Users className="w-3.5 h-3.5" />
                </button>
                <button
                  data-testid="nav-members-btn"
                  onClick={() => navigate('/operator/members')}
                  className="p-1.5 rounded text-gray-400 hover:text-[#6F8C00] hover:bg-[#F7FBEA] transition-colors"
                  title="Turning Point Members"
                >
                  <Award className="w-3.5 h-3.5" />
                </button>
                <button
                  data-testid="nav-tournaments-btn"
                  onClick={() => navigate('/operator/tournaments')}
                  className="p-1.5 rounded text-gray-400 hover:text-amber-600 hover:bg-amber-50 transition-colors"
                  title="Manage Tournaments"
                >
                  <Trophy className="w-3.5 h-3.5" />
                </button>
                <button
                  data-testid="nav-facebook-btn"
                  onClick={() => navigate('/operator/facebook')}
                  className="p-1.5 rounded text-gray-400 hover:text-brand-teal hover:bg-brand-teal-tint transition-colors"
                  title="Facebook Auto-Post"
                >
                  <Facebook className="w-3.5 h-3.5" />
                </button>
                <button
                  data-testid="nav-settings-btn"
                  onClick={() => navigate('/operator/settings')}
                  className="p-1.5 rounded text-gray-400 hover:text-gray-500 hover:bg-gray-100 transition-colors"
                >
                  <Settings className="w-3.5 h-3.5" />
                </button>
              </>
            )}
            <button
              data-testid="logout-btn"
              onClick={() => { logout(); navigate('/'); }}
              className="p-1.5 rounded text-gray-400 hover:text-status-expired hover:bg-status-expired-bg transition-colors"
            >
              <LogOut className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>

      {/* Overdue indicator - thin red line at top */}
      {urgencyStates.hasOverdue && (
        <div className="absolute top-0 left-0 right-0 h-1 bg-status-held z-30" data-testid="overdue-indicator" />
      )}

      {/* Ops Status Bar - Mode-based styling (CALM vs ATTENTION) */}
      <div 
        className={`border-b px-4 sticky top-[49px] z-10 transition-all duration-300 ${
          headerMode === 'attention'
            ? 'bg-gradient-to-r from-slate-50 to-gray-50 border-gray-300/60 py-3 shadow-sm'
            : 'bg-gray-50/50 border-gray-200/60 py-2'
        }`} 
        data-testid="ops-status-bar"
        data-header-mode={headerMode}
      >
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          {/* Status Counters - Priority masking logic */}
          {/* Priority order: Action Required > Need Approval > Awaiting Payment > Empty Soon > Confirmed Today */}
          {/* When higher priority exists, hide all lower priority chips */}
          {(() => {
            // Determine which chips to show based on priority masking
            const hasActionRequired = hasBlockingIssues;
            const hasNeedApproval = opsStatusCounts.needApproval > 0;
            const hasAwaitingPayment = opsStatusCounts.awaitingPayment > 0;
            const hasEmptySoon = opsStatusCounts.emptySoon > 0;
            const hasConfirmedToday = opsStatusCounts.confirmedToday > 0;
            
            // Priority masking: only show highest priority and below only if no higher exists
            const showActionRequired = hasActionRequired;
            const showNeedApproval = !hasActionRequired && hasNeedApproval;
            const showAwaitingPayment = !hasActionRequired && !hasNeedApproval && hasAwaitingPayment;
            const showEmptySoon = !hasActionRequired && !hasNeedApproval && !hasAwaitingPayment && hasEmptySoon;
            const showConfirmedToday = !hasActionRequired && !hasNeedApproval && !hasAwaitingPayment && !hasEmptySoon && hasConfirmedToday;
            
            // If nothing to show, we're truly calm
            const showNothingButCalm = !showActionRequired && !showNeedApproval && !showAwaitingPayment && !showEmptySoon && !showConfirmedToday;
            
            return (
              <div className={`flex items-center gap-2.5 transition-opacity duration-300 ${headerMode === 'calm' ? 'opacity-60' : 'opacity-100'}`}>
                {/* Action Required - HIGHEST PRIORITY (P1) */}
                {showActionRequired && (
                  <div className="flex items-center gap-1.5">
                    <button
                      data-testid="ops-filter-action-required"
                      onClick={() => {
                        setOpsFilter(opsFilter === 'actionRequired' ? null : 'actionRequired');
                        if (opsFilter !== 'actionRequired') handleAttentionAnchor('actionRequired');
                      }}
                      className={`flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm transition-all border-2 ${
                        opsFilter === 'actionRequired'
                          ? 'bg-status-held-bg border-status-held text-status-held font-bold shadow-md'
                          : 'bg-status-held-bg/60 border-status-held/60 text-status-held font-semibold shadow-sm hover:shadow-md'
                      }`}
                      style={{ animation: 'pulse-outline-orange 4s ease-in-out infinite' }}
                    >
                      <span className="w-2.5 h-2.5 rounded-full bg-status-held" />
                      Action Required
                      <span className="px-2 py-0.5 rounded-md text-xs font-bold bg-status-held/30 text-status-held">
                        {blockingIssues.length}
                      </span>
                    </button>
                    {/* Global indicator pill for other days */}
                    {globalAlerts?.counts?.blocking_issue?.other_days > 0 && (
                      <button
                        data-testid="global-action-required"
                        onClick={() => setCrossDayTasksOpen(true)}
                        className="px-2 py-1 rounded-full text-[11px] bg-status-held-bg text-status-held hover:bg-status-held/20 font-medium transition-colors border border-status-held/30"
                      >
                        Other days: {globalAlerts.counts.blocking_issue.other_days}
                      </button>
                    )}
                  </div>
                )}

                {/* Need Approval - P2 */}
                {showNeedApproval && (
                  <div className="flex items-center gap-1.5">
                    <button
                      data-testid="ops-filter-need-approval"
                      onClick={() => {
                        setOpsFilter(opsFilter === 'needApproval' ? null : 'needApproval');
                        if (opsFilter !== 'needApproval') handleAttentionAnchor('needApproval');
                      }}
                      className={`flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm transition-all border-2 ${
                        opsFilter === 'needApproval'
                          ? `${statusStyles.pending_approval.chip.active} font-bold shadow-md`
                          : `${statusStyles.pending_approval.chip.active} font-semibold shadow-sm hover:shadow-md`
                      }`}
                      style={{ animation: 'pulse-outline 6s ease-in-out infinite' }}
                    >
                      <span className={`w-2.5 h-2.5 rounded-full ${statusStyles.pending_approval.chip.dot}`} />
                      Need Approval
                      <span className={`px-2 py-0.5 rounded-md text-xs font-bold ${statusStyles.pending_approval.chip.badge}`}>
                        {opsStatusCounts.needApproval}
                      </span>
                    </button>
                    {/* Global indicator pill for other days */}
                    {globalAlerts?.counts?.pending_approval?.other_days > 0 && (
                      <button
                        data-testid="global-need-approval"
                        onClick={() => setCrossDayTasksOpen(true)}
                        className="px-2 py-1 rounded-full text-[11px] bg-status-review-bg text-status-review hover:opacity-80 font-medium transition-colors border border-status-review/20"
                      >
                        Other days: {globalAlerts.counts.pending_approval.other_days}
                      </button>
                    )}
                  </div>
                )}
                
                {/* Awaiting Payment - P3 */}
                {showAwaitingPayment && (
                  <div className="flex items-center gap-1.5">
                    <button
                      data-testid="ops-filter-awaiting-payment"
                      onClick={() => {
                        setOpsFilter(opsFilter === 'awaitingPayment' ? null : 'awaitingPayment');
                        if (opsFilter !== 'awaitingPayment') handleAttentionAnchor('awaitingPayment');
                      }}
                      className={`flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm transition-all border-2 ${
                        urgencyStates.hasOverdue
                          ? opsFilter === 'awaitingPayment'
                            ? `${statusStyles.overdue.chip.active} font-bold shadow-md`
                            : `${statusStyles.overdue.chip.active} font-semibold shadow-sm hover:shadow-md`
                          : urgencyStates.hasStartingSoon
                            ? opsFilter === 'awaitingPayment'
                              ? `${statusStyles.starting_soon.chip.active} font-bold shadow-md`
                              : `${statusStyles.starting_soon.chip.active} font-semibold shadow-sm hover:shadow-md`
                            : opsFilter === 'awaitingPayment'
                              ? `${statusStyles.pending_payment.chip.active} font-bold shadow-md`
                              : `${statusStyles.pending_payment.chip.active} font-semibold shadow-sm hover:shadow-md`
                      }`}
                    >
                      <span className={`w-2.5 h-2.5 rounded-full ${
                        urgencyStates.hasOverdue
                          ? statusStyles.overdue.chip.dot
                          : urgencyStates.hasStartingSoon
                            ? statusStyles.starting_soon.chip.dot
                            : statusStyles.pending_payment.chip.dot
                      }`} />
                      {urgencyStates.hasOverdue ? 'Overdue!' : 
                       urgencyStates.hasStartingSoon ? 'Starting Soon!' : 'Awaiting Payment'}
                      <span className={`px-2 py-0.5 rounded-md text-xs font-bold ${
                        urgencyStates.hasOverdue
                          ? statusStyles.overdue.chip.badge
                          : urgencyStates.hasStartingSoon
                            ? statusStyles.starting_soon.chip.badge
                            : statusStyles.pending_payment.chip.badge
                      }`}>
                        {opsStatusCounts.awaitingPayment}
                      </span>
                    </button>
                    {/* Global indicator pill for other days */}
                    {globalAlerts?.counts?.pending_payment?.other_days > 0 && (
                      <button
                        data-testid="global-awaiting-payment"
                        onClick={() => setCrossDayTasksOpen(true)}
                        className="px-2 py-1 rounded-full text-[11px] bg-status-held-bg text-status-held hover:opacity-80 font-medium transition-colors border border-status-held/20"
                      >
                        Other days: {globalAlerts.counts.pending_payment.other_days}
                      </button>
                    )}
                  </div>
                )}
                
                {/* Empty Soon - P4 (no scroll anchor - empty slots aren't bookings) */}
                {showEmptySoon && (
                  <button
                    data-testid="ops-filter-empty-soon"
                    onClick={() => setOpsFilter(opsFilter === 'emptySoon' ? null : 'emptySoon')}
                    className={`flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm transition-all border-2 ${
                      opsFilter === 'emptySoon'
                        ? `${statusStyles.empty.chip.active} font-bold shadow-md`
                        : `${statusStyles.empty.chip.active} font-semibold shadow-sm hover:shadow-md`
                    }`}
                  >
                    <span className={`w-2.5 h-2.5 rounded-full ${statusStyles.empty.chip.dot}`} />
                    Empty Soon
                    <span className={`px-2 py-0.5 rounded-md text-xs font-bold ${statusStyles.empty.chip.badge}`}>
                      {opsStatusCounts.emptySoon}
                    </span>
                  </button>
                )}
                
                {/* Confirmed Today - P5 (lowest, only when nothing else) */}
                {showConfirmedToday && (
                  <button
                    data-testid="ops-filter-confirmed-today"
                    onClick={() => {
                      setOpsFilter(opsFilter === 'confirmedToday' ? null : 'confirmedToday');
                      if (opsFilter !== 'confirmedToday') handleAttentionAnchor('confirmedToday');
                    }}
                    className={`flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm transition-all border-2 ${
                      opsFilter === 'confirmedToday'
                        ? `${statusStyles.confirmed.chip.active} font-bold shadow-md`
                        : `${statusStyles.confirmed.chip.active} font-semibold shadow-sm hover:shadow-md`
                    }`}
                  >
                    <span className={`w-2.5 h-2.5 rounded-full ${statusStyles.confirmed.chip.dot}`} />
                    Confirmed Today
                    <span className={`px-2 py-0.5 rounded-md text-xs font-bold ${statusStyles.confirmed.chip.badge}`}>
                      {opsStatusCounts.confirmedToday}
                    </span>
                  </button>
                )}

                {opsFilter && (
                  <button
                    data-testid="ops-filter-clear"
                    onClick={() => {
                      setOpsFilter(null);
                      setAnchoredBookingId(null);
                      setAnchorMatches([]);
                      setAnchorIndex(0);
                    }}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm text-gray-500 hover:text-gray-700 hover:bg-white border border-transparent hover:border-gray-300 transition-all"
                  >
                    <X className="w-4 h-4" />
                    Clear
                  </button>
                )}

                {/* Anchor Navigation - show when multiple matches */}
                {anchorMatches.length > 1 && (
                  <div className="flex items-center gap-1 bg-gray-100 rounded-lg px-2 py-1" data-testid="anchor-nav">
                    <button
                      onClick={() => handleAnchorNav('prev')}
                      className="p-1 rounded hover:bg-gray-200 text-gray-600 transition-colors"
                      data-testid="anchor-nav-prev"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <span className="text-xs font-medium text-gray-600 min-w-[40px] text-center">
                      {anchorIndex + 1} / {anchorMatches.length}
                    </span>
                    <button
                      onClick={() => handleAnchorNav('next')}
                      className="p-1 rounded hover:bg-gray-200 text-gray-600 transition-colors"
                      data-testid="anchor-nav-next"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                )}

                {/* Calm mode indicator - truly nothing to show */}
                {showNothingButCalm && headerMode === 'calm' && (
                  <span className="text-xs text-gray-400 font-medium">All clear</span>
                )}

                {/* Global Alert Summary - shows when there are alerts on other days */}
                {(() => {
                  const totalOtherDays = (globalAlerts?.counts?.blocking_issue?.other_days || 0) +
                    (globalAlerts?.counts?.pending_approval?.other_days || 0) +
                    (globalAlerts?.counts?.pending_payment?.other_days || 0);
                  
                  // Show amber indicator whenever there are ANY alerts on other days
                  // This ensures operators don't miss cross-day tasks even when busy with today's schedule
                  if (totalOtherDays > 0) {
                    return (
                      <button
                        data-testid="global-alerts-summary"
                        onClick={() => setCrossDayTasksOpen(true)}
                        className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium bg-status-held-bg text-status-held hover:opacity-80 border border-status-held/20 transition-all"
                        style={{ animation: 'pulse-outline-amber 4s ease-in-out infinite' }}
                      >
                        <Bell className="w-3.5 h-3.5" />
                        Other days: {totalOtherDays}
                      </button>
                    );
                  }
                  return null;
                })()}
              </div>
            );
          })()}

          {/* Next Event Predictor - Mode-based emphasis + Context Locking */}
          {nextEvent && isToday && (
            <div className={`flex items-center gap-3 px-4 py-2 rounded-lg border shadow-sm transition-all duration-300 ${
              headerMode === 'calm' ? 'opacity-50' : 'opacity-100'
            } ${
              nextEvent.displayStatus === 'confirmed' ? statusStyles.confirmed.next.bg :
              nextEvent.displayStatus === 'partialPayment' ? statusStyles.partial_payment.next.bg :
              nextEvent.displayStatus === 'overdue' ? statusStyles.overdue.next.bg :
              nextEvent.displayStatus === 'startingSoon' ? statusStyles.starting_soon.next.bg :
              nextEvent.displayStatus === 'awaitingPayment' ? statusStyles.pending_payment.next.bg :
              nextEvent.displayStatus === 'pendingApproval' ? statusStyles.pending_approval.next.bg :
              nextEvent.displayStatus === 'empty' ? statusStyles.empty.next.bg :
              nextEvent.displayStatus === 'openPlay' ? statusStyles.open_play.next.bg :
              'bg-white/80'
            } border-gray-200`} data-testid="next-event-predictor" data-context-locked={nextEvent.isContextLocked ? 'true' : 'false'}>
              <span className={`text-xs font-medium uppercase tracking-wide ${nextEvent.isContextLocked ? 'text-gray-600' : 'text-gray-500'}`}>
                {nextEvent.isContextLocked ? 'Viewing' : 'Next'}
              </span>
              <span className="text-lg font-bold text-gray-900">{formatHour(nextEvent.startHour)}</span>
              <span className="text-gray-400">•</span>
              <span className="font-semibold text-gray-700">{nextEvent.courtName}</span>
              <span className="text-gray-400">•</span>
              <span className="font-semibold text-gray-800">{nextEvent.playerName || '—'}</span>
              <span className={`font-bold text-sm px-2.5 py-1 rounded-md ${
                nextEvent.displayStatus === 'confirmed' ? `${statusStyles.confirmed.next.bg} ${statusStyles.confirmed.next.text}` :
                nextEvent.displayStatus === 'partialPayment' ? `${statusStyles.partial_payment.next.bg} ${statusStyles.partial_payment.next.text}` :
                nextEvent.displayStatus === 'overdue' ? `${statusStyles.overdue.next.bg} ${statusStyles.overdue.next.text}` :
                nextEvent.displayStatus === 'startingSoon' ? `${statusStyles.starting_soon.next.bg} ${statusStyles.starting_soon.next.text}` :
                nextEvent.displayStatus === 'awaitingPayment' ? `${statusStyles.pending_payment.next.bg} ${statusStyles.pending_payment.next.text}` :
                nextEvent.displayStatus === 'pendingApproval' ? `${statusStyles.pending_approval.next.bg} ${statusStyles.pending_approval.next.text}` :
                nextEvent.displayStatus === 'empty' ? `${statusStyles.empty.next.bg} ${statusStyles.empty.next.text}` :
                nextEvent.displayStatus === 'openPlay' ? `${statusStyles.open_play.next.bg} ${statusStyles.open_play.next.text}` :
                'bg-gray-100 text-gray-600'
              }`}>
                {nextEvent.displayStatus === 'confirmed' ? statusStyles.confirmed.next.label :
                 nextEvent.displayStatus === 'partialPayment' ? statusStyles.partial_payment.next.label :
                 nextEvent.displayStatus === 'overdue' ? statusStyles.overdue.next.label :
                 nextEvent.displayStatus === 'startingSoon' ? statusStyles.starting_soon.next.label :
                 nextEvent.displayStatus === 'awaitingPayment' ? statusStyles.pending_payment.next.label :
                 nextEvent.displayStatus === 'pendingApproval' ? statusStyles.pending_approval.next.label :
                 nextEvent.displayStatus === 'empty' ? statusStyles.empty.next.label :
                 nextEvent.displayStatus === 'openPlay' ? statusStyles.open_play.next.label :
                 nextEvent.statusLabel}
              </span>
            </div>
          )}
          {!isToday && (
            <div className="text-sm text-gray-500 font-medium">
              Viewing {selectedDate}
            </div>
          )}
        </div>
      </div>

      {/* Grid - Card background with clean layout */}
      <div className="max-w-7xl mx-auto px-4 py-4 overflow-x-auto">
        <div ref={scheduleGridRef} className="min-w-[600px] bg-white rounded-xl border border-border-subtle/60 overflow-hidden" data-testid="schedule-grid">
          {/* Column Headers */}
          <div className="grid border-b border-border-subtle/30" style={{ gridTemplateColumns: `80px repeat(${courts.length}, 1fr)` }}>
            <div className="p-3"></div>
            {courts.map((court, idx) => (
              <div 
                key={court.id} 
                className={`text-sm font-semibold text-text-primary p-3 text-center ${idx > 0 ? 'border-l border-border-subtle/30' : ''}`}
              >
                {court.name}
              </div>
            ))}
          </div>

          {/* Schedule Grid with continuous time flow */}
          <div className="relative">
            {/* Current time "Now" indicator - subtle guide */}
            {isToday && currentHour >= hours[0] && currentHour <= hours[hours.length - 1] && (
              <div 
                className="absolute left-0 right-0 z-20 flex items-center pointer-events-none" 
                style={{ top: `${(currentHour - hours[0]) * 70 + 35}px` }}
              >
                {/* Circle marker on time column */}
                <div className="w-[80px] flex justify-end pr-2">
                  <div className="w-2 h-2 rounded-full bg-brand-teal" />
                </div>
                <div className="flex-1 h-px bg-brand-teal/50" />
                <span className="px-2.5 py-0.5 bg-brand-teal text-white text-[10px] font-medium rounded-full mx-1">Now</span>
                <div className="flex-1 h-px bg-brand-teal/50" />
              </div>
            )}

            {/* Time Rows */}
            {hours.map((hour, rowIdx) => {
              const isCurrentHour = isToday && hour === currentHour;
              return (
                <div
                  key={hour}
                  data-hour={hour}
                  className="grid border-b border-border-subtle/30 last:border-b-0"
                  style={{ gridTemplateColumns: `80px repeat(${courts.length}, 1fr)`, minHeight: '70px' }}
                >
                  {/* Time label */}
                  <div className={`text-sm font-medium p-3 flex items-start ${isCurrentHour ? 'text-brand-teal font-semibold' : 'text-text-secondary/70'}`}>
                    {formatHour(hour)}
                  </div>

                  {/* Court cells */}
                  {courts.map((court, colIdx) => {
                    const raw = getCellData(court.id, hour);
                    const booking = filters.bookings ? raw.booking : null;
                    const openPlay = filters.openPlay ? raw.openPlay : null;
                    const block = filters.blocks ? raw.block : null;
                    const hasContent = booking || openPlay || block;

                    // Calculate if this is the start of a multi-hour block
                    const isBlockStartHour = block && isBlockStart(block, hour);
                    const isBookingStartHour = !block && booking && isBookingStart(booking, hour);
                    const isOpenPlayStartHour = !block && openPlay && isOpenPlayStart(openPlay, court.id, hour);
                    
                    // Calculate block height for spanning elements
                    // end_hour is EXCLUSIVE, so duration = end_hour - start_hour
                    const blockDuration = block ? (block.end_hour - block.start_hour) : 0;
                    const bookingDuration = booking ? (booking.end_hour - booking.start_hour) : 0;
                    const openPlayDuration = openPlay ? (openPlay.end_hour - openPlay.start_hour) : 0;

                    // Shared card styling: identical radius and padding
                    const cardBase = 'absolute inset-x-1 top-1 z-10 rounded-xl p-3 text-left transition-all cursor-pointer';

                    // Ops filter: Check if this cell matches the active filter
                    const matchesOpsFilter = () => {
                      if (!opsFilter) return true;
                      if (opsFilter === 'emptySoon') {
                        // Highlight empty slots starting within 60 minutes
                        if (isToday && !hasContent && (hour === currentHour || hour === currentHour + 1)) {
                          return true;
                        }
                        return false;
                      }
                      if (booking && filteredBookingIds instanceof Set) {
                        return filteredBookingIds.has(booking.id);
                      }
                      return false;
                    };
                    const isFiltered = opsFilter && !matchesOpsFilter();
                    const filterOpacity = isFiltered ? 'opacity-20' : '';

                    return (
                      <div
                        key={`${court.id}-${hour}`}
                        className={`relative p-1 ${colIdx > 0 ? 'border-l border-border-subtle/30' : ''} ${filterOpacity} transition-opacity`}
                      >
                        {/* Empty Soon highlight */}
                        {opsFilter === 'emptySoon' && isToday && !hasContent && (hour === currentHour || hour === currentHour + 1) && (
                          <div className="absolute inset-1 rounded-xl bg-gray-200/50 border-2 border-dashed border-gray-400 flex items-center justify-center">
                            <span className="text-xs text-gray-500 font-medium">Available</span>
                          </div>
                        )}
                        {/* BLOCKED - Coral (infrastructural, not event-based) */}
                        {isBlockStartHour && (
                          <button
                            data-testid={`cell-${court.id}-${hour}`}
                            onClick={() => handleCellClick(court.id, hour)}
                            className={`${cardBase} border-l-[6px] border-l-coral/70 hover:border-l-coral/90`}
                            style={{ 
                              height: `${blockDuration * 70 - 8}px`,
                              background: `repeating-linear-gradient(
                                135deg,
                                rgba(255, 107, 74, 0.04),
                                rgba(255, 107, 74, 0.04) 4px,
                                transparent 4px,
                                transparent 8px
                              )`
                            }}
                          >
                            <p className="font-normal text-sm" style={{ color: '#9CA3AF' }}>{block.reason || 'Maintenance Block'}</p>
                            <p className="text-xs text-text-secondary/40 mt-1">
                              {formatHour(block.start_hour)}–{formatHour(block.end_hour - 1)}
                            </p>
                          </button>
                        )}
                        
                        {/* BOOKING - Different visual treatment based on 3-stage workflow */}
                        {isBookingStartHour && (() => {
                          const status = booking.status || 'confirmed';
                          const isRecurring = !!(booking.series_id || booking.recurring_series_id || booking.is_recurring);
                          
                          // === DERIVED UI STATE (3-stage workflow) ===
                          let uiState;
                          if (status === 'confirmed' || booking.payment_status === 'paid') {
                            uiState = 'CONFIRMED';
                          } else if (status === 'pending_approval' || booking.proof_uploaded_at || booking.payment_proof) {
                            uiState = 'PENDING_APPROVAL';
                          } else {
                            uiState = 'PENDING_PAYMENT';
                          }
                          
                          const isPendingPayment = uiState === 'PENDING_PAYMENT';
                          const isPendingApproval = uiState === 'PENDING_APPROVAL';
                          
                          // Status-based styling
                          let blockBg, badgeBg, badgeText, badgeLabel;
                          
                          if (isRecurring) {
                            // Recurring bookings: Indigo color system with session payment status
                            const sessionPaid = booking.payment_status === 'paid';
                            blockBg = ''; // Using inline style
                            badgeBg = '';
                            badgeText = '';
                            badgeLabel = sessionPaid ? 'Paid' : 'Due';
                          } else if (isPendingPayment) {
                            blockBg = 'bg-status-held-bg'; // payBg
                            badgeBg = 'text-white';
                            badgeText = '';
                            badgeLabel = 'Pending Payment';
                          } else if (isPendingApproval) {
                            blockBg = 'bg-status-review-bg'; // apprBg  
                            badgeBg = 'text-white';
                            badgeText = '';
                            badgeLabel = 'Needs Approval';
                          } else {
                            // Confirmed
                            blockBg = 'bg-brand-teal-tint'; // brandTealSoft
                            badgeBg = booking.payment_status === 'paid' ? 'bg-brand-teal/20' : 'bg-gray-100';
                            badgeText = booking.payment_status === 'paid' ? 'text-brand-teal font-medium' : 'text-text-secondary';
                            badgeLabel = getBookingStatusLabel(booking, isToday, currentHour);
                          }
                          
                          // Badge background inline style
                          const badgeBgStyle = isRecurring
                            ? { backgroundColor: 'rgba(110,106,201,0.12)', color: '#5C58B8' }
                            : isPendingPayment 
                              ? { backgroundColor: '#C4841A' }  // payPillBg
                              : isPendingApproval 
                                ? { backgroundColor: '#6E6AC9' }  // apprPillBg
                                : {};
                          
                          // Border color based on status
                          const borderColor = isRecurring
                            ? '#6E6AC9'
                            : isPendingPayment 
                              ? '#F4A62A'  // payBorder
                              : isPendingApproval 
                                ? '#6E6AC9'  // apprBorder
                                : '#0F8F8D'; // brandTeal
                          
                          return (
                            <button
                              data-testid={`cell-${court.id}-${hour}`}
                              data-booking-id={booking.id}
                              onClick={() => handleCellClick(court.id, hour)}
                              className={`${cardBase} ${blockBg} hover:-translate-y-0.5 hover:brightness-[1.08] ${anchoredBookingId === booking.id ? 'attention-anchor-highlight' : ''}`}
                              style={{ 
                                height: `${bookingDuration * 70 - 8}px`,
                                ...(isRecurring ? {
                                  background: 'rgba(110,106,201,0.12)',
                                  borderLeft: '6px solid transparent',
                                  borderImage: 'repeating-linear-gradient(to bottom, #6E6AC9 0px, #6E6AC9 4px, #A8A6E0 4px, #A8A6E0 8px) 6',
                                  boxShadow: '0 4px 10px rgba(110,106,201,0.1)',
                                } : {
                                  borderLeft: `6px ${isPendingPayment ? 'dashed' : 'solid'} ${borderColor}`,
                                  boxShadow: isPendingPayment ? 'none' : '0 4px 10px rgba(0,0,0,0.08)',
                                }),
                                ...(isPendingPayment && !isRecurring && {
                                  backgroundImage: `repeating-linear-gradient(
                                    45deg,
                                    transparent,
                                    transparent 4px,
                                    rgba(245, 158, 11, 0.06) 4px,
                                    rgba(245, 158, 11, 0.06) 8px
                                  )`
                                })
                              }}
                              title={isRecurring ? 'Recurring booking' : undefined}
                            >
                              <p className="text-[13px] font-medium" style={{ color: '#374151' }}>
                                {formatHour(booking.start_hour)}–{formatHour(booking.end_hour)}
                              </p>
                              <div className="flex items-center gap-1 mt-0.5">
                                {/* Coaching badge */}
                                {(booking.booking_type === 'coaching' || booking.coach_name) && (
                                  <span className="text-sm" title={`Coach: ${booking.coach_name}`}>🎓</span>
                                )}
                                <p className="font-semibold text-sm" style={{ color: '#1F2937' }}>{booking.player_name}</p>
                                {isRecurring && (
                                  <Repeat className="w-3 h-3" style={{ color: '#6E6AC9' }} title="Recurring booking" />
                                )}
                              </div>
                              <span 
                                className={`inline-block mt-1.5 text-[10px] px-1.5 py-0.5 rounded ${badgeBg} ${badgeText}`}
                                style={badgeBgStyle}
                              >
                                {badgeLabel}
                              </span>
                              {/* Countdown for pending payment */}
                              {isPendingPayment && booking.hold_expires_at && (
                                <CountdownTimer expiresAt={booking.hold_expires_at} />
                              )}
                              {/* Helper text for pending approval */}
                              {isPendingApproval && (
                                <p className="text-[10px] mt-0.5" style={{ color: '#6E6AC9', opacity: 0.7 }}>Proof received</p>
                              )}
                            </button>
                          );
                        })()}
                        
                        {/* OPEN PLAY - Blue (medium visual weight) */}
                        {isOpenPlayStartHour && (() => {
                          const current = openPlay.players?.length || 0;
                          const max = openPlay.max_players || 16;
                          const spotsLeft = max - current;
                          let spotsText = '';
                          if (current === 0) spotsText = `${max} spots open`;
                          else if (current >= max) spotsText = 'Full';
                          else if (spotsLeft <= 2) spotsText = 'Almost full';
                          else spotsText = `${spotsLeft} spots left`;
                          
                          return (
                            <button
                              data-testid={`cell-${court.id}-${hour}`}
                              onClick={() => handleCellClick(court.id, hour)}
                              className={`${cardBase} bg-open-play/[0.04] border-l-4 border-l-open-play/60 hover:shadow-[0_0_8px_rgba(63,175,116,0.15)]`}
                              style={{ height: `${openPlayDuration * 70 - 8}px` }}
                            >
                              <p className="text-[13px] font-medium" style={{ color: '#4B5563' }}>
                                {formatHour(openPlay.start_hour)}–{formatHour(openPlay.end_hour - 1)}
                              </p>
                              <p className="font-medium text-sm mt-0.5" style={{ color: '#374151' }}>Open Play</p>
                              <p className="text-[11px] text-text-secondary/60 mt-0.5">Drop-in session</p>
                              {openPlay.max_players && (
                                <p className={`text-xs mt-1 ${current >= max ? 'text-coral' : 'text-open-play/70'}`}>
                                  {spotsText}
                                </p>
                              )}
                            </button>
                          );
                        })()}
                        
                        {/* Empty cell - clickable for new schedule */}
                        {!hasContent && (
                          <button
                            data-testid={`cell-${court.id}-${hour}`}
                            onClick={() => handleCellClick(court.id, hour)}
                            className="w-full h-full min-h-[62px] flex items-center justify-center text-text-secondary/0 hover:text-text-secondary/50 hover:bg-gray-50/50 rounded-xl transition-all group"
                          >
                            <span className="flex items-center gap-1 text-sm opacity-0 group-hover:opacity-100 transition-opacity">
                              <Plus className="w-4 h-4" />
                              Add schedule
                            </span>
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Action Drawer */}
      <Sheet open={drawerOpen} onOpenChange={(open) => {
        setDrawerOpen(open);
        if (!open) { setNoteText(''); setReleaseConfirmOpen(false); setOpInlineEditMode(false); }
      }}>
        <SheetContent 
          key={`drawer-${selectedCell?.booking?.id || 'none'}-${selectedCell?.booking?.status || 'none'}-${selectedCell?.booking?.proof_uploaded_at || 'none'}-${selectedCell?.booking?.payment_proof || 'none'}`}
          side="right" 
          className="w-[400px] sm:w-[440px] overflow-y-auto" 
          data-testid="action-drawer"
        >
          <SheetHeader>
            <SheetTitle className="font-heading flex items-center justify-between">
              {selectedCell?.booking ? (
                <span data-testid="drawer-header">{formatHour(selectedCell.booking.start_hour)}–{formatHour(selectedCell.booking.end_hour)} &middot; {selectedCell.courtName}</span>
              ) : (
                <span data-testid="drawer-header">{selectedCell && formatHour(selectedCell.hour)} &middot; {selectedCell?.courtName}</span>
              )}
              {/* Prev / Next navigation for bookings */}
              {selectedCell?.booking && (() => {
                const courtBookings = bookings
                  .filter(b => b.court_id === selectedCell.booking.court_id)
                  .sort((a, b) => a.start_hour - b.start_hour);
                const idx = courtBookings.findIndex(b => b.id === selectedCell.booking.id);
                const prev = idx > 0 ? courtBookings[idx - 1] : null;
                const next = idx < courtBookings.length - 1 ? courtBookings[idx + 1] : null;
                return (
                  <div className="flex items-center gap-1">
                    <Button
                      data-testid="drawer-prev-booking"
                      variant="ghost" size="sm" className="rounded-lg h-8 w-8 p-0"
                      disabled={!prev}
                      onClick={() => {
                        if (prev) {
                          setSelectedCell({ courtId: prev.court_id, courtName: selectedCell.courtName, hour: prev.start_hour, booking: prev, openPlay: null, block: null });
                          setNoteText('');
                          setReleaseConfirmOpen(false);
                        }
                      }}
                    >
                      <ChevronRight className="w-4 h-4 rotate-180" />
                    </Button>
                    <Button
                      data-testid="drawer-next-booking"
                      variant="ghost" size="sm" className="rounded-lg h-8 w-8 p-0"
                      disabled={!next}
                      onClick={() => {
                        if (next) {
                          setSelectedCell({ courtId: next.court_id, courtName: selectedCell.courtName, hour: next.start_hour, booking: next, openPlay: null, block: null });
                          setNoteText('');
                          setReleaseConfirmOpen(false);
                        }
                      }}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                );
              })()}
            </SheetTitle>
          </SheetHeader>

          <div className="mt-6 space-y-4">
            {selectedCell?.booking ? (
              <>
                {/* 1. STATUS / PAYMENT - Booking Details Card */}
                {(() => {
                  const booking = selectedCell.booking;
                  const status = booking.status || 'confirmed';
                  const isRecurring = !!(booking.series_id || booking.recurring_series_id || booking.is_recurring);
                  
                  // === DERIVED UI STATE (3-stage workflow) ===
                  let uiState;
                  if (isRecurring) {
                    // Recurring: separate payment states
                    if (booking.payment_status === 'paid') uiState = 'SESSION_PAID';
                    else if (booking.payment_status === 'proof_submitted' || booking.payment_proof) uiState = 'SESSION_PAYMENT_SUBMITTED';
                    else uiState = 'SESSION_UNPAID';
                  } else {
                    if (status === 'confirmed' || booking.payment_status === 'paid') {
                      uiState = 'CONFIRMED';
                    } else if (status === 'pending_approval' || booking.proof_uploaded_at || booking.payment_proof) {
                      uiState = 'PENDING_APPROVAL';
                    } else {
                      uiState = 'PENDING_PAYMENT';
                    }
                  }
                  
                  const isPendingPayment = uiState === 'PENDING_PAYMENT';
                  const isPendingApproval = uiState === 'PENDING_APPROVAL';
                  const isConfirmed = uiState === 'CONFIRMED';
                  
                  // Determine styling based on state
                  let badgeClass, badgeLabel, cardBgClass, leftBorderClass, accentColor, badgeBgStyle, backdropColor;
                  
                  if (isRecurring) {
                    badgeClass = 'text-sm px-3 py-1';
                    badgeLabel = 'Recurring';
                    cardBgClass = 'bg-white';
                    leftBorderClass = '';
                    accentColor = 'text-status-review';
                    badgeBgStyle = { backgroundColor: 'rgba(110,106,201,0.12)', color: '#5C58B8' };
                    backdropColor = 'bg-status-review-bg';
                  } else if (isPendingPayment) {
                    badgeClass = 'text-white text-sm px-3 py-1';
                    badgeLabel = 'Pending Payment';
                    cardBgClass = 'bg-white';
                    leftBorderClass = 'border-l-[6px] border-l-[#F4A62A]';
                    accentColor = 'text-[#C4841A]';
                    badgeBgStyle = { backgroundColor: '#C4841A' };
                    backdropColor = 'bg-status-held-bg';
                  } else if (isPendingApproval) {
                    badgeClass = 'text-white text-sm px-3 py-1';
                    badgeLabel = 'Needs Approval';
                    cardBgClass = 'bg-white';
                    leftBorderClass = 'border-l-[6px] border-l-[#6E6AC9]';
                    accentColor = 'text-[#6E6AC9]';
                    badgeBgStyle = { backgroundColor: '#6E6AC9' };
                    backdropColor = 'bg-status-review-bg';
                  } else {
                    badgeClass = 'text-white text-sm px-3 py-1';
                    badgeLabel = 'Confirmed';
                    cardBgClass = 'bg-white';
                    leftBorderClass = 'border-l-[6px] border-l-brand-teal';
                    accentColor = 'text-brand-teal';
                    badgeBgStyle = { backgroundColor: '#0F8F8D' };
                    backdropColor = 'bg-brand-teal-tint';
                  }
                  
                  return (
                    <div 
                      className={`rounded-2xl overflow-hidden border border-border-subtle shadow-sm ${isRecurring ? '' : leftBorderClass}`}
                      style={isRecurring ? { borderLeft: '6px solid transparent', borderImage: 'repeating-linear-gradient(to bottom, #6E6AC9 0px, #6E6AC9 4px, #A8A6E0 4px, #A8A6E0 8px) 6' } : {}}
                    >
                      {/* Soft tinted backdrop */}
                      <div className={`${cardBgClass} ${backdropColor} p-4 space-y-3`}>
                        {/* Header: Name + Status Badge */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {/* Coaching badge */}
                            {(booking.booking_type === 'coaching' || booking.coach_name) && (
                              <span className="text-xl" title="Coaching Session">🎓</span>
                            )}
                            {/* Ball Machine Training badge */}
                            {booking.booking_intent === 'ball_machine' && (
                              <span className="text-xl" title="Ball Machine Training" data-testid="drawer-ball-machine-badge">🎯</span>
                            )}
                            <span className="text-lg font-bold text-text-primary">{booking.player_name}</span>
                          </div>
                          <Badge className={badgeClass} style={badgeBgStyle}>{badgeLabel}</Badge>
                        </div>
                        
                        {/* Recurring Weekly badge */}
                        {isRecurring && (
                          <span 
                            className="inline-block text-xs font-medium px-2.5 py-1 rounded-full"
                            style={{ backgroundColor: 'rgba(110,106,201,0.12)', color: '#5C58B8' }}
                            data-testid="recurring-weekly-badge"
                          >
                            Recurring &middot; Weekly
                          </span>
                        )}
                        
                        {/* Coaching Info: Coach name + Rate type + Source */}
                        {(booking.booking_type === 'coaching' || booking.coach_name) && (
                          <div className="rounded-lg px-3 py-2 space-y-1" style={{ backgroundColor: 'rgba(14,111,108,0.12)', border: '1px solid rgba(14,111,108,0.3)' }} data-testid="drawer-coaching-info">
                            <div className="flex items-center gap-2 text-sm" style={{ color: '#0E6F6C' }}>
                              <span className="font-medium">Coach:</span>
                              <span>{booking.coach_name}</span>
                            </div>
                            {booking.is_promo_rate !== undefined && (
                              <div className="flex items-center gap-2 text-xs" style={{ color: booking.is_promo_rate ? '#C4841A' : '#0E6F6C' }}>
                                <span>{booking.is_promo_rate ? '⭐ Promo Rate (6AM-6PM)' : 'Regular Rate'}</span>
                                <span>· ₱{booking.hourly_rate}/hr</span>
                              </div>
                            )}
                            {booking.source === 'coach_qr' && (
                              <div className="flex items-center gap-2 text-xs" style={{ color: '#0E6F6C' }} data-testid="drawer-source-coach-qr">
                                <span className="font-medium">Source:</span>
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider" style={{ backgroundColor: 'rgba(14,111,108,0.15)', color: '#0E6F6C' }}>Coach QR</span>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* PENDING PAYMENT: Expiry countdown bar (cream bg) */}
                        {isPendingPayment && booking.hold_expires_at && (
                          <div className="bg-status-held-bg rounded-lg px-3 py-2 flex items-center gap-2">
                            <Timer className="w-4 h-4" style={{ color: '#C4841A' }} />
                            <span className="text-sm" style={{ color: '#C4841A' }}>Expires in </span>
                            <CountdownTimer expiresAt={booking.hold_expires_at} />
                          </div>
                        )}
                        
                        {/* PENDING APPROVAL: Proof received message (purple bg) */}
                        {isPendingApproval && (
                          <div className="bg-status-review-bg rounded-lg px-3 py-2 flex items-center gap-2">
                            <Check className="w-4 h-4" style={{ color: '#6E6AC9' }} />
                            <span className="text-sm" style={{ color: '#6E6AC9' }}>Proof received — verify payment</span>
                          </div>
                        )}
                        
                        {/* Time + Court on same line */}
                        <div className={`flex items-center gap-4 text-sm ${accentColor}`}>
                          <span className="flex items-center gap-1.5">
                            <Clock className="w-4 h-4" />
                            {formatHour(booking.start_hour)}–{formatHour(booking.end_hour)}
                          </span>
                          <span className="flex items-center gap-1.5">
                            <MapPin className="w-4 h-4" />
                            {selectedCell.courtName}
                          </span>
                        </div>
                        
                        {/* Total + Due on same line */}
                        {(() => {
                          const total = booking.amount || 0;
                          const paid = booking.paid_amount || 0;
                          const due = total - paid;
                          return (
                            <div className="flex items-center gap-4 text-sm">
                              <span className="text-text-secondary">Total</span>
                              <span className="font-bold text-text-primary">{formatPrice(total)}</span>
                              {due > 0 && (
                                <>
                                  <span className="text-border-subtle">|</span>
                                  <span className="text-text-secondary">Due</span>
                                  <span className="font-bold text-text-primary">{formatPrice(due)}</span>
                                </>
                              )}
                            </div>
                          );
                        })()}
                        
                        {/* Email + Member badge/action */}
                        {booking.player_email && (
                          <div className="space-y-1.5">
                            <div className="text-sm text-text-secondary">{booking.player_email}</div>
                            {booking.is_member ? (
                              <div
                                data-testid="member-badge"
                                className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[11px] font-semibold"
                                style={{ backgroundColor: '#EEF6C6', color: '#6F8C00' }}
                              >
                                <Award className="w-3 h-3" />
                                Turning Point Member
                              </div>
                            ) : (
                              <button
                                data-testid="add-as-member-btn"
                                onClick={async () => {
                                  try {
                                    const { addMemberFromBookingApi } = await import('../lib/api');
                                    const res = await addMemberFromBookingApi(booking.id);
                                    toast.success(res.data?.created ? 'Added as Turning Point Member' : 'Member updated');
                                    loadTimeline();
                                  } catch (err) {
                                    toast.error(err.response?.data?.detail || 'Failed to add member');
                                  }
                                }}
                                className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[11px] font-semibold border transition-colors"
                                style={{ borderColor: '#D9E6B5', color: '#6F8C00', backgroundColor: '#F7FBEA' }}
                              >
                                <Plus className="w-3 h-3" />
                                Add as Member
                              </button>
                            )}
                          </div>
                        )}
                        
                        {/* Phone (tappable) */}
                        {booking.player_phone && (
                          <div className="flex items-center gap-2">
                            <a 
                              href={`tel:${booking.player_phone}`}
                              data-testid="phone-call-link"
                              className={`flex items-center gap-1.5 text-sm font-medium ${accentColor}`}
                            >
                              <Phone className="w-4 h-4" />
                              {booking.player_phone}
                            </a>
                            <button
                              data-testid="phone-copy-btn"
                              onClick={() => {
                                navigator.clipboard.writeText(booking.player_phone)
                                  .then(() => toast.success('Phone copied'))
                                  .catch(() => toast.error('Failed to copy'));
                              }}
                              className="text-text-secondary hover:text-brand-teal p-1"
                              title="Copy phone number"
                            >
                              <Copy className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        )}
                        
                        {/* Existing note display */}
                        {booking.notes && (
                          <div className="text-xs text-text-secondary bg-app-bg rounded-lg p-2 italic">
                            <StickyNote className="w-3 h-3 inline mr-1" />{booking.notes}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}

                {/* Payment Proof Image - Screenshot */}
                {selectedCell.booking.payment_proof && selectedCell.booking.payment_proof_type !== 'receipt_text' && (
                  <div className="rounded-2xl overflow-hidden border border-border-subtle" data-testid="payment-proof-preview">
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}${selectedCell.booking.payment_proof}`}
                      alt="GCash payment proof"
                      className="w-full max-h-64 object-contain bg-gray-50"
                      onClick={() => {
                        setPaymentProofUrl(`${process.env.REACT_APP_BACKEND_URL}${selectedCell.booking.payment_proof}`);
                        setPaymentProofPlayer(selectedCell.booking.player_name);
                        setPaymentProofOpen(true);
                      }}
                      style={{ cursor: 'pointer' }}
                    />
                    <div className="px-3 py-2 bg-gray-100 text-xs space-y-1">
                      <div className="flex justify-between items-center">
                        <span className="font-medium text-gray-700">Uploaded</span>
                        <span className="text-gray-600">
                          {selectedCell.booking.proof_uploaded_at 
                            ? new Date(selectedCell.booking.proof_uploaded_at).toLocaleString('en-PH', { dateStyle: 'short', timeStyle: 'short' })
                            : 'N/A'}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="font-medium text-gray-700">Amount Expected</span>
                        <span className="text-gray-900 font-bold">₱{selectedCell.booking.amount || 0}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Payment Proof - Receipt Text */}
                {selectedCell.booking.payment_proof_type === 'receipt_text' && (
                  <div className="rounded-2xl border border-border-subtle overflow-hidden" data-testid="receipt-text-proof">
                    <div className="px-4 py-3 bg-status-review-bg border-b border-status-review/20">
                      <p className="text-xs font-semibold text-status-review">GCash Receipt Text</p>
                    </div>
                    <div className="px-4 py-3 space-y-2 bg-white">
                      {selectedCell.booking.parsed_amount != null && (
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-gray-500">Amount</span>
                          <span className="font-bold text-gray-900">₱{Number(selectedCell.booking.parsed_amount).toLocaleString()}</span>
                        </div>
                      )}
                      {selectedCell.booking.parsed_timestamp && (
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-gray-500">Time</span>
                          <span className="font-medium text-gray-700">{selectedCell.booking.parsed_timestamp}</span>
                        </div>
                      )}
                      {selectedCell.booking.parsed_reference && (
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-gray-500">Reference</span>
                          <span className="font-mono text-gray-700">{selectedCell.booking.parsed_reference}</span>
                        </div>
                      )}
                      {selectedCell.booking.parsed_receiver_phone && (
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-gray-500">Receiver</span>
                          <span className="text-gray-700">{selectedCell.booking.parsed_receiver_phone}</span>
                        </div>
                      )}
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-500">Amount Expected</span>
                        <span className="font-bold text-gray-900">₱{selectedCell.booking.amount || 0}</span>
                      </div>
                    </div>
                    {selectedCell.booking.receipt_text_raw && (
                      <details className="border-t border-gray-100">
                        <summary className="px-4 py-2 text-xs text-gray-400 cursor-pointer hover:text-gray-600">View raw receipt</summary>
                        <pre className="px-4 py-2 text-xs text-gray-500 whitespace-pre-wrap bg-gray-50">{selectedCell.booking.receipt_text_raw}</pre>
                      </details>
                    )}
                  </div>
                )}

                {/* === PAYMENT WORKFLOW ACTIONS === */}
                {(() => {
                  const booking = selectedCell.booking;
                  const status = booking.status || 'confirmed';
                  const isRecurring = !!(booking.series_id || booking.recurring_series_id || booking.is_recurring);
                  
                  if (isRecurring) {
                    // === RECURRING SESSION PAYMENT CARD ===
                    const sessionState = booking.payment_status === 'paid' ? 'SESSION_PAID'
                      : (booking.payment_status === 'proof_submitted' || booking.payment_proof) ? 'SESSION_PAYMENT_SUBMITTED'
                      : 'SESSION_UNPAID';
                    
                    return (
                      <>
                        {sessionState === 'SESSION_UNPAID' && (
                          <div 
                            className="rounded-[14px] p-[18px] mt-3"
                            style={{ background: 'rgba(244,166,42,0.12)', border: '1px solid rgba(244,166,42,0.45)' }}
                            data-testid="session-unpaid-card"
                          >
                            <p className="text-sm font-semibold text-gray-900">Payment Due</p>
                            <p className="text-xs text-gray-500 mt-1">Payment required for this session</p>
                            <Button
                              data-testid="mark-session-paid-btn"
                              onClick={async () => {
                                setActionLoading(true);
                                try {
                                  await markPaidApi(booking.id);
                                  toast.success('Session marked as paid');
                                  await refreshAndUpdateDrawer(booking.id);
                                } catch (err) {
                                  toast.error('Failed to mark paid');
                                }
                                setActionLoading(false);
                              }}
                              disabled={actionLoading}
                              className="mt-3 w-full rounded-[10px] py-2.5 text-sm font-semibold text-white"
                              style={{ backgroundColor: '#0F8F8D' }}
                            >
                              Mark Session Paid
                            </Button>
                          </div>
                        )}
                        
                        {sessionState === 'SESSION_PAYMENT_SUBMITTED' && (
                          <div
                            className="rounded-[14px] p-[18px] mt-3"
                            style={{ background: '#EFF6FF', border: '1px solid #BFDBFE' }}
                            data-testid="session-submitted-card"
                          >
                            <p className="text-sm font-semibold text-gray-900">Payment Submitted</p>
                            <p className="text-xs text-gray-500 mt-1">Waiting for approval</p>
                            <div className="flex gap-2 mt-3">
                              {booking.payment_proof && (
                                <Button
                                  data-testid="view-proof-btn"
                                  variant="outline"
                                  onClick={() => {
                                    setPaymentProofUrl(`${process.env.REACT_APP_BACKEND_URL}${booking.payment_proof}`);
                                    setPaymentProofPlayer(booking.player_name);
                                    setPaymentProofOpen(true);
                                  }}
                                  className="flex-1 rounded-[10px] py-2.5 text-sm font-medium"
                                >
                                  View Proof
                                </Button>
                              )}
                              <Button
                                data-testid="approve-session-btn"
                                onClick={async () => {
                                  setActionLoading(true);
                                  try {
                                    await approveBookingApi(booking.id);
                                    toast.success('Session payment approved');
                                    await refreshAndUpdateDrawer(booking.id);
                                  } catch (err) {
                                    toast.error('Failed to approve');
                                  }
                                  setActionLoading(false);
                                }}
                                disabled={actionLoading}
                                className="flex-1 rounded-[10px] py-2.5 text-sm font-semibold text-white"
                                style={{ backgroundColor: '#0F8F8D' }}
                              >
                                Approve Payment
                              </Button>
                            </div>
                          </div>
                        )}
                        
                        {sessionState === 'SESSION_PAID' && (
                          <div
                            className="rounded-[14px] p-[18px] mt-3"
                            style={{ background: '#ECFDF5', border: '1px solid #A7F3D0' }}
                            data-testid="session-paid-card"
                          >
                            <div className="flex items-center gap-2">
                              <Check className="w-5 h-5 text-brand-teal" />
                              <p className="text-sm font-semibold text-gray-900">Session Paid</p>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">Payment confirmed for this session</p>
                            <button
                              data-testid="undo-session-paid-btn"
                              onClick={async () => {
                                setActionLoading(true);
                                try {
                                  await updateBookingApi(booking.id, { payment_status: 'unpaid', paid_amount: 0 });
                                  toast.success('Payment status reverted');
                                  await refreshAndUpdateDrawer(booking.id);
                                } catch (err) {
                                  toast.error('Failed to undo');
                                }
                                setActionLoading(false);
                              }}
                              disabled={actionLoading}
                              className="text-xs text-gray-400 hover:text-gray-600 mt-2 transition-colors"
                            >
                              Undo
                            </button>
                          </div>
                        )}
                      </>
                    );
                  }
                  
                  // === NON-RECURRING PAYMENT WORKFLOW ===
                  // Derive UI state
                  let uiState;
                  if (booking.payment_status === 'partial') {
                    uiState = 'PARTIAL_PAYMENT';
                  } else if (status === 'confirmed' && booking.payment_status === 'pending') {
                    // Operator-created "Unpaid" booking — court is held but payment is outstanding
                    uiState = 'UNPAID_CONFIRMED';
                  } else if (status === 'confirmed' || booking.payment_status === 'paid') {
                    uiState = 'CONFIRMED';
                  } else if (status === 'pending_approval' || booking.payment_status === 'proof_submitted') {
                    uiState = 'PENDING_APPROVAL';
                  } else {
                    uiState = 'PENDING_PAYMENT';
                  }
                  
                  return (
                    <>
                      {/* PARTIAL PAYMENT: Extension unpaid */}
                      {uiState === 'PARTIAL_PAYMENT' && (() => {
                        const total = booking.amount || 0;
                        const paid = booking.paid_amount || 0;
                        const due = total - paid;
                        return (
                          <div 
                            className="rounded-[14px] p-[18px]"
                            style={{ background: 'rgba(244,166,42,0.12)', border: '1px solid rgba(244,166,42,0.45)' }}
                            data-testid="partial-payment-card"
                          >
                            <p className="text-sm font-semibold text-gray-900">Extension Payment Due</p>
                            <p className="text-xs text-gray-500 mt-1">
                              Paid {formatPrice(paid)} of {formatPrice(total)}
                            </p>
                            <div className="flex items-center justify-between bg-white rounded-lg px-3 py-2 mt-2 border border-amber-200">
                              <span className="text-xs text-gray-500">Balance due</span>
                              <span className="text-lg font-bold text-amber-700">{formatPrice(due)}</span>
                            </div>
                            <Button
                              data-testid="confirm-extra-payment-btn"
                              onClick={async () => {
                                setActionLoading(true);
                                try {
                                  await markPaidApi(booking.id);
                                  toast.success('Full payment confirmed');
                                  await refreshAndUpdateDrawer(booking.id);
                                } catch (err) {
                                  toast.error('Failed to confirm payment');
                                }
                                setActionLoading(false);
                              }}
                              disabled={actionLoading}
                              className="mt-3 w-full rounded-[10px] py-2.5 text-sm font-semibold text-white"
                              style={{ backgroundColor: '#0F8F8D' }}
                            >
                              <DollarSign className="w-4 h-4 mr-1.5" /> Confirm Full Payment ({formatPrice(total)})
                            </Button>
                          </div>
                        );
                      })()}

                      {/* PENDING PAYMENT: Waiting for customer to upload proof */}
                      {uiState === 'PENDING_PAYMENT' && (
                        <div className="bg-status-held-bg border border-status-held/20 rounded-xl p-4 text-center">
                          <Clock className="w-8 h-8 text-status-held mx-auto mb-2" />
                          <p className="text-sm font-medium text-status-held">Waiting for Payment Proof</p>
                          <p className="text-xs text-status-held/70 mt-1">Customer needs to upload their GCash screenshot</p>
                        </div>
                      )}

                      {/* PENDING APPROVAL: Proof uploaded, show Approve/Reject buttons */}
                      {uiState === 'PENDING_APPROVAL' && canApprovePayments() && (() => {
                        const hasScreenshot = !!booking.payment_proof && booking.payment_proof_type !== 'receipt_text';
                        const hasReceiptText = booking.payment_proof_type === 'receipt_text';
                        const hasProof = hasScreenshot || hasReceiptText;
                        return (
                        <div className="space-y-3">
                          {!hasProof && (
                            <div className="bg-status-review-bg border border-status-review/20 rounded-xl p-4 text-center">
                              <p className="text-sm text-status-review">Payment proof not visible. May still be processing.</p>
                            </div>
                          )}
                          
                          {hasProof && (
                            <>
                              <div className="bg-brand-teal-tint border border-brand-teal/20 rounded-xl p-3 text-center">
                                <p className="text-xs text-brand-teal font-medium">
                                  {hasScreenshot
                                    ? 'Verify the screenshot above, then approve or reject'
                                    : 'Verify the receipt details above, then approve or reject'}
                                </p>
                              </div>
                              
                              <div className="grid grid-cols-2 gap-3">
                                <Button
                                  data-testid="drawer-reject-btn"
                                  onClick={async () => {
                                    if (!window.confirm('Reject this payment proof? Customer will need to re-upload.')) return;
                                    setActionLoading(true);
                                    try {
                                      await rejectBookingApi(booking.id);
                                      toast.success('Payment rejected. Customer can re-upload.');
                                      await refreshAndUpdateDrawer(booking.id);
                                    } catch (err) {
                                      toast.error('Failed to reject payment');
                                    }
                                    setActionLoading(false);
                                  }}
                                  variant="outline"
                                  className="rounded-xl py-4 text-sm font-semibold border-status-expired text-status-expired hover:bg-status-expired-bg"
                                  disabled={actionLoading}
                                >
                                  Reject
                                </Button>
                                <Button
                                  data-testid="drawer-approve-btn"
                                  onClick={async () => {
                                    setActionLoading(true);
                                    try {
                                      await approveBookingApi(booking.id);
                                      toast.success('Booking confirmed!');
                                      await refreshAndUpdateDrawer(booking.id);
                                    } catch (err) {
                                      toast.error('Failed to approve booking');
                                    }
                                    setActionLoading(false);
                                  }}
                                  className="rounded-xl py-4 text-sm font-semibold text-white"
                                  style={{ backgroundColor: '#0F8F8D' }}
                                  disabled={actionLoading}
                                >
                                  Approve
                                </Button>
                              </div>
                            </>
                          )}
                        </div>
                        );
                      })()}

                      {/* UNPAID CONFIRMED: Operator-created unpaid booking - court held, payment outstanding */}
                      {uiState === 'UNPAID_CONFIRMED' && (
                        <div className="space-y-3" data-testid="drawer-unpaid-confirmed">
                          <div className="border rounded-xl p-4 text-center" style={{ backgroundColor: '#FFF8E6', borderColor: '#F1CD5A' }}>
                            <Clock className="w-8 h-8 mx-auto mb-2" style={{ color: '#B8871A' }} />
                            <p className="text-sm font-medium" style={{ color: '#B8871A' }}>Confirmed · Unpaid</p>
                            <p className="text-xs mt-1" style={{ color: '#B8871A', opacity: 0.8 }}>
                              Court is held. Collect ₱{((booking.amount || 0) - (booking.paid_amount || 0)).toLocaleString()} to complete this booking.
                            </p>
                          </div>
                          {canApprovePayments() && (
                            <Button
                              data-testid="drawer-mark-paid-btn"
                              onClick={async () => {
                                setActionLoading(true);
                                try {
                                  await markPaidApi(booking.id);
                                  toast.success('Marked as paid');
                                  await refreshAndUpdateDrawer(booking.id);
                                } catch (err) {
                                  toast.error('Failed to mark paid');
                                }
                                setActionLoading(false);
                              }}
                              className="w-full rounded-xl py-4 text-sm font-semibold text-white"
                              style={{ backgroundColor: '#0F8F8D' }}
                              disabled={actionLoading}
                            >
                              Mark Paid
                            </Button>
                          )}
                        </div>
                      )}

                      {/* CONFIRMED: No payment actions needed */}
                      {uiState === 'CONFIRMED' && (
                        <div className="bg-brand-teal-tint border border-brand-teal/20 rounded-xl p-4 text-center">
                          <Check className="w-8 h-8 text-brand-teal mx-auto mb-2" />
                          <p className="text-sm font-medium text-brand-teal">Payment Confirmed</p>
                          {booking.approved_at && (
                            <p className="text-xs text-brand-teal mt-1">
                              Approved {new Date(booking.approved_at).toLocaleString('en-PH', { dateStyle: 'short', timeStyle: 'short' })}
                            </p>
                          )}
                        </div>
                      )}
                    </>
                  );
                })()}

                {/* 2. EXTEND TIME */}
                <div className="space-y-3">
                  <p className="text-xs font-semibold text-text-secondary uppercase tracking-wider">Extend Time</p>
                  <div className="flex gap-2">
                    {[1, 2, 3].map(h => {
                      const newEndHour = selectedCell.booking.end_hour + h;
                      const canExtend = canExtendByHours(selectedCell.booking, h);
                      return (
                        <button
                          key={h}
                          data-testid={`extend-${h}h-btn`}
                          onClick={() => handleExtendBooking(selectedCell.booking.id, h)}
                          disabled={actionLoading || !canExtend}
                          className={`flex-1 rounded-xl py-3 px-2 text-center transition-all border ${
                            canExtend 
                              ? 'bg-white border-border-subtle text-text-primary hover:border-brand-teal hover:bg-brand-teal hover:text-white' 
                              : 'bg-white border-border-subtle text-text-secondary/50 cursor-not-allowed'
                          }`}
                        >
                          <div className="flex items-center justify-center gap-1 font-semibold">
                            <CircleCheck className={`w-4 h-4 ${canExtend ? 'text-brand-teal' : 'opacity-50'}`} />
                            +{h}h
                          </div>
                          <div className={`text-xs mt-0.5 ${canExtend ? 'text-text-secondary' : 'text-text-secondary/50'}`}>
                            — {formatHour(newEndHour)}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* 3. MOVE COURT */}
                <div className="bg-white rounded-2xl p-4 space-y-3 border border-border-subtle">
                  <div className="flex items-center gap-2 text-sm font-semibold text-text-primary">
                    <ArrowRightLeft className="w-4 h-4" /> Move Court
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {courts.filter(c => c.id !== selectedCell.booking.court_id).map(c => {
                      const isAvailable = isCourtAvailableForMove(c.id, selectedCell.booking);
                      const freeHours = isAvailable ? getCourtFreeHours(c.id, selectedCell.booking) : 0;
                      return (
                        <button
                          key={c.id}
                          data-testid={`move-to-${c.id}`}
                          onClick={() => handleMoveBooking(selectedCell.booking.id, c.id)}
                          disabled={actionLoading || !isAvailable}
                          className={`rounded-full px-4 py-2 text-sm border transition-all flex items-center gap-1.5 ${
                            isAvailable 
                              ? 'bg-white border-border-subtle text-text-primary hover:border-brand-teal hover:text-brand-teal' 
                              : 'bg-white border-border-subtle text-text-secondary/50 cursor-not-allowed'
                          }`}
                        >
                          {isAvailable ? (
                            <CircleCheck className="w-4 h-4 text-brand-teal" />
                          ) : (
                            <CircleAlert className="w-4 h-4 text-text-secondary/50" />
                          )}
                          {c.name}
                          {isAvailable && freeHours > 0 && (
                            <span className="text-text-secondary">(free {freeHours}h)</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* 4. RELEASE BOOKING - coral centered link */}
                {hasPermission('release_booking') && (
                  <div className="py-2">
                    {releaseConfirmOpen ? (
                      <div className="space-y-3 bg-white rounded-2xl p-4 border border-border-subtle" data-testid="release-confirm-panel">
                        <p className="text-sm text-text-secondary text-center">Free this time for new players?</p>
                        <div className="flex gap-2">
                          <Button
                            data-testid="release-cancel-btn"
                            onClick={() => setReleaseConfirmOpen(false)}
                            variant="outline"
                            size="sm"
                            className="flex-1 rounded-xl border-border-subtle text-text-primary"
                          >
                            Keep Booking
                          </Button>
                          <Button
                            data-testid="release-confirm-btn"
                            onClick={async () => {
                              await handleReleaseBooking(selectedCell.booking.id);
                              setReleaseConfirmOpen(false);
                            }}
                            variant="outline"
                            size="sm"
                            className="flex-1 rounded-xl text-coral border-coral hover:bg-coral-bg"
                          >
                            Yes, Release
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <button
                        data-testid="drawer-release-btn"
                        onClick={() => setReleaseConfirmOpen(true)}
                        className="w-full text-center text-sm text-coral font-medium py-2 transition-colors hover:text-coral/80"
                      >
                        Release Booking
                      </button>
                    )}
                  </div>
                )}

                {/* 5. ADD NOTE */}
                <div className="bg-white rounded-2xl p-4 space-y-3 border border-border-subtle">
                  <div className="flex items-center gap-2 text-sm font-semibold text-text-primary">
                    <StickyNote className="w-4 h-4" /> Add Note
                  </div>
                  {/* Recent notes suggestions */}
                  {(() => {
                    const recentNotes = getRecentNotes().filter(n => n !== selectedCell.booking.notes);
                    if (recentNotes.length === 0) return null;
                    return (
                      <div className="flex gap-1.5 flex-wrap" data-testid="note-suggestions">
                        {recentNotes.map((note, idx) => (
                          <button
                            key={idx}
                            data-testid={`note-suggestion-${idx}`}
                            onClick={() => setNoteText(note)}
                            className="text-xs px-3 py-1 bg-app-bg hover:bg-brand-teal/10 rounded-full text-text-secondary hover:text-brand-teal transition-colors"
                          >
                            {note}
                          </button>
                        ))}
                      </div>
                    );
                  })()}
                  <div className="flex gap-2">
                    <Input
                      data-testid="note-input"
                      value={noteText}
                      onChange={(e) => setNoteText(e.target.value)}
                      placeholder="e.g. VIP, tournament prep"
                      className="rounded-xl text-sm flex-1 border-border-subtle bg-app-bg"
                      onFocus={() => { if (!noteText && selectedCell.booking.notes) setNoteText(selectedCell.booking.notes); }}
                    />
                    <Button
                      data-testid="save-note-btn"
                      onClick={() => handleSaveNote(selectedCell.booking.id)}
                      className="rounded-xl bg-brand-teal hover:bg-brand-teal/90 text-white px-6"
                      disabled={actionLoading || !noteText.trim()}
                    >
                      Save
                    </Button>
                  </div>
                </div>
              </>
            ) : selectedCell?.openPlay ? (
              <>
                {/* ===== HEADER: Identity + Status ===== */}
                <div className="bg-white rounded-2xl p-4 space-y-3 border border-border-subtle shadow-sm">
                  {/* Identity + Status Badge + Refresh */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-lg font-bold text-text-primary">Open Play</span>
                      <button
                        data-testid="refresh-open-play-btn"
                        onClick={() => refreshAndUpdateOpenPlayDrawer(selectedCell.openPlay.id)}
                        className="p-1 rounded-full text-text-secondary hover:text-open-play hover:bg-open-play/10 transition-colors"
                        title="Refresh player list"
                      >
                        <RefreshCw className="w-4 h-4" />
                      </button>
                    </div>
                    <Badge className={
                      (() => {
                        const op = selectedCell.openPlay;
                        const isPastDate = selectedDate < getTodayPHT();
                        const isEnded = isPastDate || (isToday && currentHour >= op.end_hour);
                        if (op.status === 'cancelled') return 'bg-coral text-white px-4 py-1 text-sm';
                        if (isEnded) return 'bg-gray-200 text-gray-600 px-4 py-1 text-sm';
                        if (op.status === 'full') return 'bg-open-play text-white px-4 py-1 text-sm';
                        return 'border border-open-play text-open-play bg-white px-4 py-1 text-sm';
                      })()
                    }>
                      {(() => {
                        const op = selectedCell.openPlay;
                        const isPastDate = selectedDate < getTodayPHT();
                        const isEnded = isPastDate || (isToday && currentHour >= op.end_hour);
                        if (op.status === 'cancelled') return 'Cancelled';
                        if (isEnded) return 'Ended';
                        if (op.status === 'full') return 'Full';
                        return 'Open';
                      })()}
                    </Badge>
                  </div>
                  
                  {/* Time + Court on same line */}
                  <div className="flex items-center gap-4 text-sm text-open-play">
                    <span className="flex items-center gap-1.5">
                      <Clock className="w-4 h-4" />
                      {formatHour(selectedCell.openPlay.start_hour)}–{formatHour(selectedCell.openPlay.end_hour - 1)}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <MapPin className="w-4 h-4" />
                      {selectedCell.courtName}
                    </span>
                  </div>
                  
                  {/* Context: Price + Capacity */}
                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-text-secondary">Price</span>
                    <span className="font-bold text-text-primary">{formatPrice(selectedCell.openPlay.price_per_player)}/player</span>
                    <span className="text-border-subtle">|</span>
                    <span className="text-text-secondary">Capacity</span>
                    <span className="font-bold text-text-primary">{selectedCell.openPlay.players?.length || 0}/{selectedCell.openPlay.max_players}</span>
                  </div>
                  
                  {/* Subtitle + Status indicator */}
                  <div className="flex items-center justify-between text-sm text-text-secondary">
                    <span>Drop-in session • Anyone can join</span>
                    {(() => {
                      const op = selectedCell.openPlay;
                      const isPastDate = selectedDate < getTodayPHT();
                      const isSessionEnded = isPastDate || (isToday && currentHour >= op.end_hour);
                      const isSessionStarted = isToday && currentHour >= op.start_hour && currentHour < op.end_hour;
                      if (isSessionEnded) {
                        return <span className="text-xs text-gray-400 flex items-center gap-1"><span className="w-1.5 h-1.5 bg-gray-300 rounded-full"></span>Ended</span>;
                      } else if (isSessionStarted) {
                        return <span className="text-xs text-text-secondary/60 flex items-center gap-1"><span className="w-1.5 h-1.5 bg-open-play rounded-full animate-pulse"></span>Live</span>;
                      } else {
                        return <span className="text-xs text-text-secondary/60 flex items-center gap-1"><span className="w-1.5 h-1.5 bg-brand-teal rounded-full"></span>Upcoming</span>;
                      }
                    })()}
                  </div>
                </div>

                {/* ===== PRIMARY ACTION: OP CODE Share ===== */}
                <div className="bg-white rounded-2xl p-4 border border-border-subtle space-y-3">
                  <div className="flex items-center gap-2 text-sm font-semibold text-text-primary">
                    <Copy className="w-4 h-4" /> Share OP Code
                  </div>
                  <p className="text-xs text-text-secondary">Players can RSVP using this link</p>
                  <div className="flex gap-2">
                    <Input
                      data-testid="op-code-url-input"
                      readOnly
                      value={`${window.location.origin}/open-play/${selectedCell.openPlay.id}`}
                      className="text-xs bg-app-bg rounded-xl flex-1 border-border-subtle"
                      onClick={(e) => e.target.select()}
                    />
                    <Button
                      data-testid="copy-vibe-link-btn"
                      onClick={() => {
                        const url = `${window.location.origin}/open-play/${selectedCell.openPlay.id}`;
                        const input = document.querySelector('[data-testid="op-code-url-input"]');
                        if (input) {
                          input.select();
                          input.setSelectionRange(0, 99999);
                        }
                        let copied = false;
                        try { copied = document.execCommand('copy'); } catch (err) { copied = false; }
                        if (!copied && navigator.clipboard) {
                          navigator.clipboard.writeText(url)
                            .then(() => toast.success('Link copied!'))
                            .catch(() => toast.info('Select the URL and press Ctrl+C'));
                        } else if (copied) {
                          toast.success('Link copied!');
                        } else {
                          toast.info('Select the URL and press Ctrl+C');
                        }
                      }}
                      className="bg-open-play hover:bg-open-play/90 text-white rounded-xl px-4"
                    >
                      Copy
                    </Button>
                  </div>
                </div>

                {/* ===== SECONDARY ACTIONS: Player Management ===== */}
                <div className="bg-white rounded-2xl p-4 space-y-3 border border-border-subtle">
                  <div className="flex items-center gap-2 text-sm font-semibold text-text-primary">
                    <Users className="w-4 h-4" /> Players ({selectedCell.openPlay.players?.length || 0})
                  </div>
                  
                  {/* Player List */}
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {(selectedCell.openPlay.players || []).map(player => (
                      <div key={player.id} className="flex items-center justify-between bg-app-bg rounded-xl px-3 py-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-medium text-text-primary">{player.name}</span>
                            {player.source === 'op_code' && (
                              <Badge className="border border-open-play text-open-play bg-white text-[10px]">OP</Badge>
                            )}
                          </div>
                          <div className="flex gap-2 mt-0.5 flex-wrap">
                            {player.checked_in && <Badge className="bg-open-play/10 text-open-play text-[10px]">Checked in</Badge>}
                            {player.paid && <Badge className="bg-open-play text-white text-[10px]">Paid</Badge>}
                            {!player.paid && player.payment_proof && (
                              <Badge 
                                className="bg-open-play/10 text-open-play text-[10px] cursor-pointer hover:bg-open-play/20"
                                onClick={() => {
                                  const filename = player.payment_proof.replace('uploads/', '');
                                  setPaymentProofUrl(`${process.env.REACT_APP_BACKEND_URL}/api/uploads/${filename}`);
                                  setPaymentProofPlayer(player.name);
                                  setPaymentProofOpen(true);
                                }}
                              >
                                View Proof
                              </Badge>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-1 shrink-0">
                          {hasPermission('checkin') && !player.checked_in && (
                            <Button size="sm" variant="ghost" onClick={() => handleCheckinPlayer(selectedCell.openPlay.id, player.id)} className="h-7 w-7 p-0" title="Check In">
                              <Check className="w-3.5 h-3.5 text-open-play" />
                            </Button>
                          )}
                          {hasPermission('mark_paid') && !player.paid && (
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              onClick={() => handlePayPlayer(selectedCell.openPlay.id, player.id)} 
                              className={`h-7 w-7 p-0 ${player.payment_proof ? 'bg-open-play/10 border border-open-play' : ''}`}
                              title={player.payment_proof ? "Payment proof uploaded - Click to verify" : "Mark as Paid"}
                            >
                              <span className="font-bold text-sm text-open-play">₱</span>
                            </Button>
                          )}
                          {hasPermission('manage_open_play') && (
                            <Button size="sm" variant="ghost" onClick={() => handleRemovePlayer(selectedCell.openPlay.id, player.id)} className="h-7 w-7 p-0" title="Remove">
                              <UserMinus className="w-3.5 h-3.5 text-coral" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                    
                    {/* Waitlist */}
                    {(selectedCell.openPlay.waitlist || []).length > 0 && (
                      <>
                        <p className="text-xs font-medium text-open-play uppercase tracking-wider mt-3 pt-3 border-t border-border-subtle">Waitlist</p>
                        {(selectedCell.openPlay.waitlist || []).map((player, idx) => (
                          <div key={player.id} className="flex items-center justify-between bg-open-play/5 rounded-xl px-3 py-2">
                            <span className="text-sm font-medium text-text-primary">#{idx + 1} {player.name}</span>
                            {hasPermission('manage_open_play') && (
                              <Button size="sm" variant="ghost" onClick={() => handleRemovePlayer(selectedCell.openPlay.id, player.id)} className="h-7 w-7 p-0">
                                <UserMinus className="w-3.5 h-3.5 text-coral" />
                              </Button>
                            )}
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                  
                  {/* Add Player */}
                  {hasPermission('manage_open_play') && (
                    <div className="flex gap-2 pt-2 border-t border-border-subtle">
                      <Input
                        data-testid="add-player-input"
                        value={addPlayerName}
                        onChange={(e) => setAddPlayerName(e.target.value)}
                        placeholder="Add player name"
                        className="rounded-xl text-sm border-border-subtle bg-app-bg flex-1"
                      />
                      <Button
                        data-testid="add-player-btn"
                        onClick={() => handleAddPlayerToOpenPlay(selectedCell.openPlay.id)}
                        className="rounded-xl bg-open-play hover:bg-open-play/90 text-white px-4"
                      >
                        <UserPlus className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>

                {/* Edit Session - Inline Mode */}
                {hasPermission('manage_open_play') && (
                  <div className="bg-white rounded-2xl p-4 space-y-3 border border-border-subtle">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm font-semibold text-text-primary">
                        <Pencil className="w-4 h-4" /> Edit Session
                      </div>
                      {opInlineEditMode && (
                        <button
                          data-testid="cancel-edit-open-play-btn"
                          onClick={() => setOpInlineEditMode(false)}
                          className="text-xs text-text-secondary hover:text-text-primary"
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                    
                    {opInlineEditMode ? (
                      /* Inline Edit Form */
                      <div className="space-y-3" data-testid="open-play-inline-edit-form">
                        {/* Time Selection */}
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs text-text-secondary">Start</Label>
                            <Select value={editOpStartHour} onValueChange={setEditOpStartHour}>
                              <SelectTrigger className="rounded-xl mt-1 h-9 text-sm" data-testid="inline-edit-op-start">
                                <SelectValue placeholder="Start" />
                              </SelectTrigger>
                              <SelectContent>
                                {hours.map(h => (
                                  <SelectItem key={h} value={String(h)}>{formatHour(h)}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-xs text-text-secondary">End</Label>
                            <Select value={editOpEndHour} onValueChange={setEditOpEndHour}>
                              <SelectTrigger className="rounded-xl mt-1 h-9 text-sm" data-testid="inline-edit-op-end">
                                <SelectValue placeholder="End" />
                              </SelectTrigger>
                              <SelectContent>
                                {hours.filter(h => {
                                  const start = parseInt(editOpStartHour);
                                  return isNaN(start) || h >= start;
                                }).map(h => (
                                  <SelectItem key={h} value={String(h + 1)}>{formatHour(h)}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        
                        {/* Capacity & Price */}
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs text-text-secondary">Max Players</Label>
                            <Input 
                              data-testid="inline-edit-op-max" 
                              value={editOpMaxPlayers} 
                              onChange={(e) => setEditOpMaxPlayers(e.target.value)} 
                              type="number" 
                              className="rounded-xl mt-1 h-9 text-sm" 
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-text-secondary">Price (₱)</Label>
                            <Input 
                              data-testid="inline-edit-op-price" 
                              value={editOpPrice} 
                              onChange={(e) => setEditOpPrice(e.target.value)} 
                              type="number" 
                              className="rounded-xl mt-1 h-9 text-sm" 
                            />
                          </div>
                        </div>
                        
                        {/* Save Button */}
                        <Button
                          data-testid="save-edit-open-play-btn"
                          onClick={handleUpdateOpenPlayInline}
                          disabled={actionLoading}
                          className="w-full bg-open-play hover:bg-open-play/90 text-white rounded-xl"
                        >
                          {actionLoading ? 'Saving...' : 'Save Changes'}
                        </Button>
                      </div>
                    ) : (
                      /* Toggle to Edit Mode */
                      <Button
                        data-testid="edit-open-play-btn"
                        onClick={() => {
                          setEditOpStartHour(String(selectedCell.openPlay.start_hour));
                          setEditOpEndHour(String(selectedCell.openPlay.end_hour));
                          setEditOpMaxPlayers(String(selectedCell.openPlay.max_players));
                          setEditOpPrice(String(selectedCell.openPlay.price_per_player));
                          setOpInlineEditMode(true);
                        }}
                        variant="outline"
                        className="w-full rounded-xl text-open-play border-open-play hover:bg-open-play/5"
                      >
                        Change Time, Capacity, or Price
                      </Button>
                    )}
                  </div>
                )}

                {/* ===== DESTRUCTIVE ACTION: Cancel Session ===== */}
                {hasPermission('manage_open_play') && (
                  <div className="py-2">
                    {releaseOpenPlayConfirmOpen ? (
                      <div className="p-3 bg-coral-bg border border-coral rounded-xl space-y-2">
                        <p className="text-xs text-coral font-medium text-center">Release this Open Play session?</p>
                        <div className="flex gap-2">
                          <Button
                            data-testid="release-openplay-cancel-btn"
                            onClick={() => setReleaseOpenPlayConfirmOpen(false)}
                            variant="outline"
                            size="sm"
                            className="flex-1 rounded-xl border-border-subtle text-text-primary"
                          >
                            Keep Session
                          </Button>
                          <Button
                            data-testid="release-openplay-confirm-btn"
                            onClick={() => executeReleaseOpenPlay(selectedCell.openPlay.id)}
                            variant="outline"
                            size="sm"
                            className="flex-1 rounded-xl text-coral border-coral hover:bg-coral-bg"
                          >
                            Yes, Release
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <button
                        data-testid="release-open-play-btn"
                        onClick={() => setReleaseOpenPlayConfirmOpen(true)}
                        className="w-full text-center text-sm text-coral font-medium py-2 transition-colors hover:text-coral/80"
                      >
                        Cancel Session
                      </button>
                    )}
                  </div>
                )}
              </>
            ) : selectedCell?.block ? (
              <>
                {/* ===== HEADER: Identity + Status ===== */}
                <div className="bg-white rounded-2xl p-4 space-y-3 border border-border-subtle shadow-sm">
                  {/* Identity + Status Badge */}
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-bold text-text-primary">Court Closed</span>
                    <Badge className={
                      selectedCell.block.series_id 
                        ? 'border border-coral text-coral bg-white px-4 py-1 text-sm'
                        : 'border border-border-subtle text-text-secondary bg-white px-4 py-1 text-sm'
                    }>
                      {selectedCell.block.series_id ? 'Recurring' : 'One-time'}
                    </Badge>
                  </div>
                  
                  {/* Time + Court on same line */}
                  <div className="flex items-center gap-4 text-sm text-text-secondary">
                    <span className="flex items-center gap-1.5">
                      <Clock className="w-4 h-4" />
                      {formatHour(selectedCell.block.start_hour)}–{formatHour(selectedCell.block.end_hour - 1)}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <MapPin className="w-4 h-4" />
                      {selectedCell.courtName}
                    </span>
                  </div>
                  
                  {/* Context: Reason */}
                  {selectedCell.block.reason && (
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-text-secondary">Reason</span>
                      <span className="font-medium text-text-primary">{selectedCell.block.reason}</span>
                    </div>
                  )}
                  
                  {/* Tournament Details */}
                  {editTournament && (
                    <div className="space-y-3 mt-1">
                      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-2">
                        <div className="flex items-center gap-2 mb-2">
                          <Trophy className="w-4 h-4 text-amber-600" />
                          <span className="font-semibold text-sm text-amber-900">{editTournament.name}</span>
                        </div>
                        <div className="text-xs text-amber-800 space-y-1.5">
                          <div className="flex justify-between"><span className="text-amber-600">Organizer</span><span>{editTournament.organizer_name}</span></div>
                          <div className="flex justify-between"><span className="text-amber-600">Dates</span><span>{editTournament.start_date} to {editTournament.end_date}</span></div>
                          <div className="flex justify-between"><span className="text-amber-600">Courts</span><span>{editTournament.court_names?.join(', ')}</span></div>
                          <div className="flex justify-between"><span className="text-amber-600">Hours</span><span>{formatHour(editTournament.start_hour)}–{formatHour(editTournament.end_hour)}</span></div>
                          <div className="flex justify-between border-t border-amber-200 pt-1.5 mt-1.5">
                            <span className="text-amber-600">Court Cost</span>
                            <span className="font-medium">₱{editTournament.court_cost?.toLocaleString()}</span>
                          </div>
                        </div>
                      </div>

                      {/* Editable Additional Charges */}
                      <div className="bg-white rounded-xl border border-border-subtle p-4 space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-semibold text-text-primary">Additional Charges</span>
                          <button
                            type="button"
                            onClick={() => setEditTCharges(prev => [...prev, { name: '', amount: '' }])}
                            className="text-xs text-amber-700 hover:text-amber-800 font-medium flex items-center gap-1"
                            data-testid="edit-t-add-charge"
                          >
                            <Plus className="w-3 h-3" /> Add
                          </button>
                        </div>
                        {editTCharges.length === 0 && (
                          <p className="text-xs text-gray-400 italic">No additional charges</p>
                        )}
                        {editTCharges.map((charge, i) => (
                          <div key={i} className="flex gap-2 items-center">
                            <Input
                              value={charge.name}
                              onChange={(e) => {
                                const updated = [...editTCharges];
                                updated[i] = { ...updated[i], name: e.target.value };
                                setEditTCharges(updated);
                              }}
                              placeholder="Charge name"
                              className="rounded-lg flex-1 h-8 text-xs"
                            />
                            <div className="relative">
                              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-400">₱</span>
                              <Input
                                type="number"
                                value={charge.amount}
                                onChange={(e) => {
                                  const updated = [...editTCharges];
                                  updated[i] = { ...updated[i], amount: e.target.value };
                                  setEditTCharges(updated);
                                }}
                                placeholder="0"
                                className="rounded-lg w-24 h-8 text-xs pl-5"
                              />
                            </div>
                            <button type="button" onClick={() => setEditTCharges(prev => prev.filter((_, j) => j !== i))} className="text-gray-400 hover:text-blocks">
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ))}
                        {/* Total */}
                        {(() => {
                          const addlTotal = editTCharges.reduce((sum, c) => sum + (parseFloat(c.amount) || 0), 0);
                          const total = (editTournament.court_cost || 0) + addlTotal;
                          return (
                            <div className="flex justify-between pt-2 border-t border-gray-100 text-sm font-bold text-gray-900">
                              <span>Total</span>
                              <span>₱{total.toLocaleString()}</span>
                            </div>
                          );
                        })()}
                        <Button
                          onClick={handleSaveTournamentCharges}
                          disabled={editTSaving}
                          className="w-full rounded-xl text-white text-sm h-9"
                          style={{ backgroundColor: '#D97706' }}
                          data-testid="save-tournament-charges"
                        >
                          {editTSaving ? 'Saving...' : 'Save Charges'}
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {/* Series indicator */}
                  {selectedCell.block.series_id && (
                    <div className="text-xs text-text-secondary flex items-center gap-1.5 bg-app-bg rounded-lg p-2">
                      <Repeat className="w-3.5 h-3.5" /> Part of a recurring series
                    </div>
                  )}
                </div>

                {/* ===== SECONDARY ACTIONS: Manage Block ===== */}
                {hasPermission('block_court') && selectedCell.block.series_id && (
                  <div className="bg-white rounded-2xl p-4 space-y-3 border border-border-subtle">
                    <div className="flex items-center gap-2 text-sm font-semibold text-text-primary">
                      <Repeat className="w-4 h-4" /> Manage Series
                    </div>
                    <Button
                      data-testid="drawer-manage-series-btn"
                      onClick={() => navigate('/operator/recurring')}
                      variant="outline"
                      className="w-full rounded-xl text-text-primary border-border-subtle hover:bg-app-bg"
                    >
                      View All Recurring Blocks
                    </Button>
                  </div>
                )}

                {/* ===== DESTRUCTIVE ACTION: Reopen Court / Cancel Tournament ===== */}
                {hasPermission('block_court') && (
                  <div className="py-2">
                    {selectedCell.block.tournament_id && editTournament ? (
                      <button
                        data-testid="drawer-cancel-tournament-btn"
                        onClick={() => handleCancelTournament(editTournament.id)}
                        className="w-full text-center text-sm text-coral font-medium py-2 transition-colors hover:text-coral/80"
                      >
                        Cancel Entire Tournament
                      </button>
                    ) : selectedCell.block.series_id ? (
                      <button
                        data-testid="drawer-remove-occurrence-btn"
                        onClick={async () => {
                          try {
                            await excludeDateFromSeriesApi(selectedCell.block.series_id, selectedCell.block.date);
                            toast.success('This occurrence removed');
                            setDrawerOpen(false);
                            loadTimeline();
                          } catch (err) {
                            toast.error('Failed to remove occurrence');
                          }
                        }}
                        className="w-full text-center text-sm text-coral font-medium py-2 transition-colors hover:text-coral/80"
                      >
                        Remove This Date Only
                      </button>
                    ) : (
                      <button
                        data-testid="drawer-remove-block-btn"
                        onClick={() => handleRemoveBlock(selectedCell.block.id)}
                        className="w-full text-center text-sm text-coral font-medium py-2 transition-colors hover:text-coral/80"
                      >
                        Reopen Court
                      </button>
                    )}
                  </div>
                )}
              </>
            ) : (
              <>
                {/* Empty Cell — creation buttons */}
                {isOperator() && (
                  <Button
                    data-testid="drawer-create-booking-btn"
                    onClick={() => setCreateBookingOpen(true)}
                    className="w-full bg-brand-teal hover:bg-brand-teal/90 text-white rounded-xl py-5"
                  >
                    <CalendarPlus className="w-4 h-4 mr-2" /> Add Booking
                  </Button>
                )}
                {isOperator() && (
                  <Button
                    data-testid="drawer-create-openplay-btn"
                    onClick={() => {
                      setOpDate(selectedDate);
                      setCreateOpenPlayOpen(true);
                    }}
                    variant="outline"
                    className="w-full rounded-xl text-open-play border-open-play hover:bg-open-play-bg"
                  >
                    <Users className="w-4 h-4 mr-2" /> Create Open Play
                  </Button>
                )}
                {canManageSchedule() && (
                  <Button
                    data-testid="drawer-block-court-btn"
                    onClick={() => setBlockCourtOpen(true)}
                    variant="outline"
                    className="w-full rounded-xl text-coral border-coral hover:bg-coral-bg"
                  >
                    <Ban className="w-4 h-4 mr-2" /> Add Block
                  </Button>
                )}
              </>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Quick Booking Dialog */}
      <Dialog open={createBookingOpen} onOpenChange={setCreateBookingOpen}>
        <DialogContent className="sm:max-w-md" data-testid="quick-booking-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Add Booking</DialogTitle>
          </DialogHeader>
          <div className="space-y-5 py-2">
            {/* PLAYER */}
            <div className="space-y-3">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-400">Player</p>
              <div>
                <Label className="text-sm text-gray-600">Name</Label>
                <Input
                  data-testid="qb-name-input"
                  value={qbName}
                  onChange={(e) => setQbName(e.target.value)}
                  placeholder="Name"
                  className="rounded-xl mt-1"
                />
              </div>
              <div>
                <Label className="text-sm text-gray-600">Phone <span className="text-gray-400 font-normal">(optional)</span></Label>
                <Input
                  data-testid="qb-phone-input"
                  value={qbPhone}
                  onChange={(e) => setQbPhone(e.target.value)}
                  placeholder="+63 9XX XXX XXXX"
                  className="rounded-xl mt-1"
                />
              </div>
            </div>

            {/* BOOKING */}
            <div className="space-y-3 border-t border-border-subtle pt-4">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-400">Booking</p>
              {selectedCell ? (
                <div className="text-sm text-gray-500">
                  {selectedCell.courtName} · {formatHour(selectedCell.hour)} · {selectedDate}
                </div>
              ) : (
                <>
                  <div>
                    <Label className="text-sm text-gray-600">Court</Label>
                    <Select value={qbCourtId} onValueChange={setQbCourtId}>
                      <SelectTrigger className="rounded-xl mt-1" data-testid="qb-court-select">
                        <SelectValue placeholder="Select court" />
                      </SelectTrigger>
                      <SelectContent>
                        {courts.map(c => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm text-gray-600">Start Time</Label>
                    <Select value={qbStartHour} onValueChange={setQbStartHour}>
                      <SelectTrigger className="rounded-xl mt-1" data-testid="qb-hour-select">
                        <SelectValue placeholder="Select time" />
                      </SelectTrigger>
                      <SelectContent>
                        {hours.map(h => (
                          <SelectItem key={h} value={String(h)}>{formatHour(h)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
              <div>
                <Label className="text-sm text-gray-600">Duration</Label>
                <Select value={qbDuration} onValueChange={setQbDuration}>
                  <SelectTrigger className="rounded-xl mt-1" data-testid="qb-duration-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 12 }, (_, i) => i + 1).map(h => (
                      <SelectItem key={h} value={String(h)}>{h} {h === 1 ? 'hour' : 'hours'}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm text-gray-600 mb-1.5 block">Booking Type</Label>
                <div className="grid grid-cols-3 gap-2" data-testid="qb-booking-type">
                  {[
                    { id: 'court', label: 'Court Rental' },
                    { id: 'coaching', label: 'Coaching' },
                    { id: 'training', label: 'Training' },
                  ].map(({ id, label }) => {
                    const active = qbBookingType === id;
                    return (
                      <button
                        key={id}
                        type="button"
                        data-testid={`qb-type-${id}`}
                        onClick={() => {
                          setQbBookingType(id);
                          if (id !== 'training') { setQbIsMember(false); setQbIsSpecialPrivilege(false); }
                        }}
                        className={`py-2 rounded-lg text-sm font-medium transition-all border ${
                          active
                            ? 'bg-brand-teal border-brand-teal text-white shadow-sm'
                            : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                        }`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>
              {/* Training-only modifiers */}
              {qbBookingType === 'training' && (
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    data-testid="qb-member-toggle"
                    onClick={() => { setQbIsMember(!qbIsMember); if (!qbIsMember) setQbIsSpecialPrivilege(false); }}
                    disabled={qbIsSpecialPrivilege}
                    className={`p-2.5 rounded-lg text-xs font-medium transition-all border text-left ${
                      qbIsMember
                        ? 'text-white'
                        : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                    style={qbIsMember ? { backgroundColor: '#6F8C00', borderColor: '#6F8C00' } : {}}
                  >
                    <div className="flex items-center gap-1"><Award className="w-3 h-3" /> Turning Point Member</div>
                    <div className="text-[10px] opacity-75 mt-0.5">₱{rules?.member_training_rate ?? 400}/hr package</div>
                  </button>
                  <button
                    type="button"
                    data-testid="qb-special-privilege-toggle"
                    onClick={() => { setQbIsSpecialPrivilege(!qbIsSpecialPrivilege); if (!qbIsSpecialPrivilege) setQbIsMember(false); }}
                    className={`p-2.5 rounded-lg text-xs font-medium transition-all border text-left ${
                      qbIsSpecialPrivilege
                        ? 'text-white'
                        : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                    style={qbIsSpecialPrivilege ? { backgroundColor: '#6F8C00', borderColor: '#6F8C00' } : {}}
                  >
                    <div className="flex items-center gap-1"><Award className="w-3 h-3" /> Special Privilege</div>
                    <div className="text-[10px] opacity-75 mt-0.5">Court + ₱{rules?.training_tp_share_per_booking ?? 100} TP share</div>
                  </button>
                </div>
              )}
            </div>

            {/* PRICING */}
            <div className="space-y-2 border-t border-border-subtle pt-4">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-400">Pricing</p>
              {(() => {
                const b = computeQbBreakdown();
                const durationInt = parseInt(qbDuration) || 1;
                const specialRaw = parseFloat(qbSpecialRateAmount);
                const specialActive = qbSpecialRateOn && !isNaN(specialRaw) && specialRaw >= 0;
                const finalTotal = specialActive ? Math.max(0, specialRaw * durationInt) : b.total;
                return (
                  <div className="rounded-xl border border-brand-teal/20 bg-brand-teal-tint/40 px-4 py-3 space-y-1" data-testid="qb-pricing-card">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Rate</span>
                      <span className="text-gray-900 font-medium">
                        {specialActive
                          ? `₱${specialRaw.toLocaleString()}/hr special`
                          : b.rateLine}
                      </span>
                    </div>
                    <div className="flex justify-between items-baseline">
                      <span className="text-sm text-gray-600">Total</span>
                      <span className="text-xl font-bold text-brand-teal" data-testid="qb-total">
                        ₱{finalTotal.toLocaleString()}
                      </span>
                    </div>
                    {qbBookingType === 'training' && (
                      <p className="text-[11px] text-gray-500 pt-0.5">Locks the ball machine — no other court can use it at this time.</p>
                    )}
                  </div>
                );
              })()}
              {/* Apply special rate override (per-hour) — used only when the auto rate is wrong */}
              {qbPaymentStatus !== 'free' && (
                <div data-testid="qb-special-rate-block" className="pt-1">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm text-gray-600">Apply special rate</Label>
                    <button
                      type="button"
                      data-testid="qb-special-rate-toggle"
                      onClick={() => {
                        const nv = !qbSpecialRateOn;
                        setQbSpecialRateOn(nv);
                        if (!nv) setQbSpecialRateAmount('');
                      }}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        qbSpecialRateOn ? 'bg-brand-teal' : 'bg-gray-200'
                      }`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        qbSpecialRateOn ? 'translate-x-6' : 'translate-x-1'
                      }`} />
                    </button>
                  </div>
                  {qbSpecialRateOn && (() => {
                    const b = computeQbBreakdown();
                    const durationInt = parseInt(qbDuration) || 1;
                    const specialRaw = parseFloat(qbSpecialRateAmount);
                    const regularHourly = b.total > 0 && durationInt > 0 ? Math.round(b.total / durationInt) : b.regularRate;
                    const specialShown = !isNaN(specialRaw) && specialRaw >= 0 ? specialRaw : null;
                    return (
                      <div className="mt-2 space-y-1.5">
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">₱</span>
                          <Input
                            data-testid="qb-special-rate-input"
                            type="number"
                            inputMode="decimal"
                            min="0"
                            step="any"
                            placeholder="e.g. 250"
                            value={qbSpecialRateAmount}
                            onChange={(e) => setQbSpecialRateAmount(e.target.value)}
                            className="rounded-xl pl-7 pr-14 h-10"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500">/hr</span>
                        </div>
                        <div className="text-[11px] text-gray-500 flex justify-between">
                          <span>Special rate: <span className="font-medium text-brand-teal">{specialShown !== null ? `₱${specialShown.toLocaleString()}/hr` : '—'}</span></span>
                          <span>Regular rate: ₱{regularHourly.toLocaleString()}/hr</span>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>

            {/* PAYMENT */}
            <div className="space-y-2 border-t border-border-subtle pt-4">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-400">Payment</p>
              <div className="grid grid-cols-3 gap-2" data-testid="qb-payment-status">
                {[
                  { id: 'paid', label: 'Paid' },
                  { id: 'unpaid', label: 'Unpaid' },
                  { id: 'free', label: 'Free' },
                ].map(({ id, label }) => {
                  const active = qbPaymentStatus === id;
                  return (
                    <button
                      key={id}
                      type="button"
                      data-testid={`qb-payment-${id}`}
                      onClick={() => {
                        setQbPaymentStatus(id);
                        if (id === 'free') { setQbSpecialRateOn(false); setQbSpecialRateAmount(''); }
                      }}
                      className={`py-2 rounded-lg text-sm font-medium transition-all border ${
                        active
                          ? id === 'paid' ? 'bg-brand-teal border-brand-teal text-white'
                            : id === 'unpaid' ? 'bg-status-held border-status-held text-white'
                            : 'bg-open-play border-open-play text-white'
                          : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              data-testid="qb-submit-btn"
              onClick={handleQuickBook}
              className="bg-brand-teal hover:bg-brand-teal-hover text-white rounded-full px-8"
            >
              Confirm &amp; Book
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Open Play Dialog */}
      <CreateOpenPlayDialog
        open={createOpenPlayOpen}
        onOpenChange={setCreateOpenPlayOpen}
        courts={courts}
        hours={hours}
        date={opDate}
        selectedCourts={opCourts}
        startHour={opStartHour}
        endHour={opEndHour}
        maxPlayers={opMaxPlayers}
        price={opPrice}
        allowWalkins={opWalkins}
        autoClose={opAutoClose}
        onDateChange={setOpDate}
        onToggleCourt={(id) => setOpCourts(prev => prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id])}
        onStartHourChange={setOpStartHour}
        onEndHourChange={setOpEndHour}
        onMaxPlayersChange={setOpMaxPlayers}
        onPriceChange={setOpPrice}
        onAllowWalkinsChange={setOpWalkins}
        onAutoCloseChange={setOpAutoClose}
        onSubmit={handleCreateOpenPlay}
      />

      {/* Edit Open Play Dialog */}
      <EditOpenPlayDialog
        open={editOpenPlayOpen}
        onOpenChange={setEditOpenPlayOpen}
        hours={hours}
        startHour={editOpStartHour}
        endHour={editOpEndHour}
        maxPlayers={editOpMaxPlayers}
        price={editOpPrice}
        onStartHourChange={setEditOpStartHour}
        onEndHourChange={setEditOpEndHour}
        onMaxPlayersChange={setEditOpMaxPlayers}
        onPriceChange={setEditOpPrice}
        onSubmit={handleUpdateOpenPlay}
      />

      {/* Block Court Dialog */}
      <BlockCourtDialog
        open={blockCourtOpen}
        onOpenChange={setBlockCourtOpen}
        selectedCell={selectedCell}
        selectedDate={selectedDate}
        courts={courts}
        hours={hours}
        courtId={blockCourtId}
        startHour={blockStartHour}
        endHour={blockEndHour}
        reason={blockReason}
        onCourtIdChange={setBlockCourtId}
        onStartHourChange={setBlockStartHour}
        onEndHourChange={setBlockEndHour}
        onReasonChange={setBlockReason}
        onSubmit={handleBlockCourt}
      />

      {/* Schedule Type Picker Dialog */}
      <Dialog open={schedulePickerOpen} onOpenChange={setSchedulePickerOpen}>
        <DialogContent className="sm:max-w-xl" data-testid="schedule-picker-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg">Add to Schedule</DialogTitle>
          </DialogHeader>
          
          {/* Context Prefill Section */}
          <div className="bg-gray-50 rounded-xl p-4 space-y-3 border border-gray-100">
            <div className="flex items-center gap-3 text-sm">
              <span className="text-gray-500 w-16">Date</span>
              <span className="font-medium text-gray-900">{selectedDate}</span>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs text-gray-500 mb-1 block">Court</Label>
                <Select value={prefillCourtId} onValueChange={setPrefillCourtId}>
                  <SelectTrigger className="rounded-lg h-9 text-sm" data-testid="prefill-court-select">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {courts.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-gray-500 mb-1 block">Start</Label>
                <Select value={prefillStartHour} onValueChange={setPrefillStartHour}>
                  <SelectTrigger className="rounded-lg h-9 text-sm" data-testid="prefill-start-select">
                    <SelectValue placeholder="Time" />
                  </SelectTrigger>
                  <SelectContent>
                    {hours.map(h => (
                      <SelectItem key={h} value={String(h)}>{formatHour(h)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-gray-500 mb-1 block">Duration</Label>
                <div className="flex gap-1">
                  {['1', '2', '3'].map(d => (
                    <button
                      key={d}
                      onClick={() => setPrefillDuration(d)}
                      className={`flex-1 h-9 rounded-lg text-sm font-medium transition-all ${
                        prefillDuration === d 
                          ? 'bg-brand-teal text-white' 
                          : 'bg-white border border-gray-200 text-gray-600 hover:border-gray-300'
                      }`}
                      data-testid={`prefill-duration-${d}h`}
                    >
                      {d}h
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Tiles - Reorganized Layout */}
          <div className="space-y-3 py-2">
            {/* Row 1: Booking tiles - Operator and Admin only */}
            {isOperator() && (
            <div className="grid grid-cols-2 gap-3">
              {[
                {
                  id: 'add-booking',
                  icon: CalendarPlus,
                  title: 'Add Booking',
                  desc: 'Reserve a court for a player',
                  pill: 'One-time',
                  borderColor: '#0F8F8D',
                  bgColor: 'rgba(15,143,141,0.12)',
                  iconColor: '#0F8F8D',
                  action: () => { 
                    setSchedulePickerOpen(false); 
                    setSelectedCell(null); 
                    setQbCourtId(prefillCourtId);
                    setQbStartHour(prefillStartHour);
                    setQbDuration(prefillDuration);
                    setCreateBookingOpen(true); 
                  },
                },
                // Recurring booking - navigate to management page (operator + admin)
                ...(isOperator() ? [{
                  id: 'manage-recurring-bookings',
                  icon: CalendarDays,
                  title: 'Recurring Bookings',
                  desc: 'Manage weekly player schedules',
                  pill: '↻ Manage',
                  borderColor: '#0F8F8D',
                  bgColor: 'rgba(15,143,141,0.12)',
                  iconColor: '#0F8F8D',
                  action: () => { 
                    setSchedulePickerOpen(false); 
                    navigate('/operator/recurring-bookings');
                  },
                }] : []),
              ].map(item => {
                const Icon = item.icon;
                const isRecurring = item.pill.includes('Recurring');
                return (
                  <button
                    key={item.id}
                    data-testid={`schedule-type-${item.id}`}
                    onClick={item.action}
                    className="flex flex-col items-start gap-2 p-4 rounded-2xl border-2 text-left transition-all hover:shadow-md hover:scale-[1.01] active:scale-[0.99]"
                    style={{ borderColor: item.borderColor, background: item.bgColor }}
                  >
                    <div className="flex items-start justify-between w-full">
                      <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-white/80">
                        <Icon className="w-4 h-4" style={{ color: item.iconColor }} />
                      </div>
                      <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
                        isRecurring 
                          ? 'bg-white/80 text-gray-600' 
                          : 'bg-white/60 text-gray-500'
                      }`}>
                        {item.pill}
                      </span>
                    </div>
                    <div>
                      <p className="font-heading text-sm font-bold text-gray-900">{item.title}</p>
                      <p className="text-xs text-gray-600 mt-0.5 leading-relaxed">{item.desc}</p>
                    </div>
                  </button>
                );
              })}
            </div>
            )}
            
            {/* Row 2: Open Play + Coaching + Tournament */}
            {isOperator() && (
            <div className="grid grid-cols-2 gap-3">
              {[
                {
                  id: 'create-open-play',
                  icon: Users,
                  title: 'Create Open Play',
                  desc: 'Group session with RSVP',
                  pill: 'One-time',
                  borderColor: '#3FAF74',
                  bgColor: 'rgba(63,175,116,0.12)',
                  iconColor: '#3FAF74',
                  action: () => { 
                    setSchedulePickerOpen(false); 
                    setOpDate(selectedDate); 
                    setOpCourts(prefillCourtId ? [prefillCourtId] : []);
                    setOpStartHour(prefillStartHour);
                    setOpEndHour(String(parseInt(prefillStartHour) + parseInt(prefillDuration)));
                    setCreateOpenPlayOpen(true); 
                  },
                },
                // Coaching session only for admin
                ...(canManageCoaching() ? [{
                  id: 'coaching-session',
                  icon: GraduationCap,
                  title: 'Coaching Session',
                  desc: 'Shareable link for coaches',
                  pill: 'Special Rate',
                  borderColor: '#0E6F6C',
                  bgColor: 'rgba(14,111,108,0.12)',
                  iconColor: '#0E6F6C',
                  action: () => { 
                    setSchedulePickerOpen(false); 
                    setCoachingSessionOpen(true); 
                  },
                }] : []),
                // Tournament - admin only
                ...(isAdmin() ? [{
                  id: 'create-tournament',
                  icon: Trophy,
                  title: 'Tournament',
                  desc: 'Multi-day, multi-court event',
                  pill: 'Event',
                  borderColor: '#D97706',
                  bgColor: 'rgba(217,119,6,0.10)',
                  iconColor: '#D97706',
                  action: () => {
                    setSchedulePickerOpen(false);
                    setTStartDate(selectedDate);
                    setTEndDate(selectedDate);
                    setTournamentOpen(true);
                  },
                }] : []),
              ].map(item => {
                const Icon = item.icon;
                const isSpecial = item.pill === 'Special Rate';
                return (
                  <button
                    key={item.id}
                    data-testid={`schedule-type-${item.id}`}
                    onClick={item.action}
                    className="flex flex-col items-start gap-2 p-4 rounded-2xl border-2 text-left transition-all hover:shadow-md hover:scale-[1.01] active:scale-[0.99]"
                    style={{ borderColor: item.borderColor, background: item.bgColor }}
                  >
                    <div className="flex items-start justify-between w-full">
                      <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-white/80">
                        <Icon className="w-4 h-4" style={{ color: item.iconColor }} />
                      </div>
                      <span 
                        className="text-[10px] font-medium px-2 py-0.5 rounded-full"
                        style={isSpecial ? { 
                          backgroundColor: 'rgba(244,166,42,0.12)', 
                          color: '#C4841A',
                          border: '1px solid rgba(244,166,42,0.3)'
                        } : item.pill === 'Event' ? {
                          backgroundColor: 'rgba(244,166,42,0.18)',
                          color: '#C4841A',
                          border: '1px solid rgba(244,166,42,0.3)'
                        } : {
                          backgroundColor: 'rgba(255,255,255,0.6)',
                          color: '#6B7280'
                        }}
                      >
                        {item.pill}
                      </span>
                    </div>
                    <div>
                      <p className="font-heading text-sm font-bold text-gray-900">{item.title}</p>
                      <p className="text-xs text-gray-600 mt-0.5 leading-relaxed">{item.desc}</p>
                    </div>
                  </button>
                );
              })}
            </div>
            )}
            
            {/* Row 3: Block tiles - Admin only */}
            {canManageSchedule() && (
            <div className="grid grid-cols-2 gap-3">
              {[
                {
                  id: 'add-block',
                  icon: Wrench,
                  title: 'Add Block',
                  desc: 'Close court for maintenance',
                  pill: 'One-time',
                  borderColor: '#5E6A75',
                  bgColor: 'rgba(94,106,117,0.12)',
                  iconColor: '#5E6A75',
                  action: () => { 
                    setSchedulePickerOpen(false); 
                    setSelectedCell(null); 
                    setBlockCourtId(prefillCourtId);
                    setBlockStartHour(prefillStartHour);
                    setBlockCourtOpen(true); 
                  },
                },
                {
                  id: 'manage-recurring-blocks',
                  icon: CalendarDays,
                  title: 'Recurring Blocks',
                  desc: 'Manage maintenance schedules',
                  pill: '↻ Manage',
                  borderColor: '#5E6A75',
                  bgColor: 'rgba(94,106,117,0.12)',
                  iconColor: '#5E6A75',
                  action: () => { 
                    setSchedulePickerOpen(false); 
                    navigate('/operator/recurring');
                  },
                },
              ].map(item => {
                const Icon = item.icon;
                const isRecurring = item.pill.includes('Recurring');
                return (
                  <button
                    key={item.id}
                    data-testid={`schedule-type-${item.id}`}
                    onClick={item.action}
                    className="flex flex-col items-start gap-2 p-4 rounded-2xl border-2 text-left transition-all hover:shadow-md hover:scale-[1.01] active:scale-[0.99]"
                    style={{ borderColor: item.borderColor, background: item.bgColor }}
                  >
                    <div className="flex items-start justify-between w-full">
                      <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-white/80">
                        <Icon className="w-4 h-4" style={{ color: item.iconColor }} />
                      </div>
                      <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
                        isRecurring 
                          ? 'bg-white/80 text-gray-600' 
                          : 'bg-white/60 text-gray-500'
                      }`}>
                        {item.pill}
                      </span>
                    </div>
                    <div>
                      <p className="font-heading text-sm font-bold text-gray-900">{item.title}</p>
                      <p className="text-xs text-gray-600 mt-0.5 leading-relaxed">{item.desc}</p>
                    </div>
                  </button>
                );
              })}
            </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Recurring Booking Dialog */}
      <Dialog open={recurringBookingOpen} onOpenChange={setRecurringBookingOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="recurring-booking-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Add Recurring Booking</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label className="text-sm text-gray-600">Player Name</Label>
                <Input data-testid="rb-inline-name" value={rbPlayerName} onChange={(e) => setRbPlayerName(e.target.value)} placeholder="e.g. John Santos" className="rounded-xl mt-1" />
              </div>
              <div>
                <Label className="text-sm text-gray-600">Email</Label>
                <Input data-testid="rb-inline-email" type="email" value={rbPlayerEmail} onChange={(e) => setRbPlayerEmail(e.target.value)} placeholder="john@email.com" className="rounded-xl mt-1" />
              </div>
              <div>
                <Label className="text-sm text-gray-600">Phone (optional)</Label>
                <Input data-testid="rb-inline-phone" value={rbPlayerPhone} onChange={(e) => setRbPlayerPhone(e.target.value)} placeholder="+63 9XX" className="rounded-xl mt-1" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-sm text-gray-600">Court</Label>
                <Select value={rbCourtId} onValueChange={setRbCourtId}>
                  <SelectTrigger className="rounded-xl mt-1" data-testid="rb-inline-court">
                    <SelectValue placeholder="Court" />
                  </SelectTrigger>
                  <SelectContent>
                    {courts.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm text-gray-600">Start Time</Label>
                <Select value={rbStartHour} onValueChange={setRbStartHour}>
                  <SelectTrigger className="rounded-xl mt-1" data-testid="rb-inline-start">
                    <SelectValue placeholder="Time" />
                  </SelectTrigger>
                  <SelectContent>
                    {hours.map(h => (
                      <SelectItem key={h} value={String(h)}>{formatHour(h)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm text-gray-600">Duration</Label>
                <Select value={rbDuration} onValueChange={setRbDuration}>
                  <SelectTrigger className="rounded-xl mt-1" data-testid="rb-inline-duration">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 12 }, (_, i) => i + 1).map(d => (
                      <SelectItem key={d} value={String(d)}>{d}h</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-sm text-gray-600 mb-2 block">Repeat on</Label>
              <div className="flex gap-1.5">
                {WEEKDAY_NAMES.map((name, i) => (
                  <button
                    key={i}
                    data-testid={`rb-inline-day-${i}`}
                    onClick={() => toggleRbWeekday(i)}
                    className={`flex-1 py-2.5 rounded-xl text-xs font-semibold transition-all ${
                      rbWeekdays.includes(i)
                        ? 'bg-brand-teal text-white shadow-md'
                        : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                    }`}
                  >
                    {name}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm text-gray-600">Repeat every</Label>
                <Select value={rbInterval} onValueChange={setRbInterval}>
                  <SelectTrigger className="rounded-xl mt-1" data-testid="rb-inline-interval">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Every week</SelectItem>
                    <SelectItem value="2">Every 2 weeks</SelectItem>
                    <SelectItem value="4">Every 4 weeks</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm text-gray-600">Start Date</Label>
                <Input data-testid="rb-inline-start-date" type="date" value={rbStartDate} onChange={(e) => setRbStartDate(e.target.value)} className="rounded-xl mt-1" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRecurringBookingOpen(false)} className="rounded-full">Cancel</Button>
            <Button
              data-testid="rb-inline-submit"
              onClick={handleCreateRecurringBooking}
              disabled={rbSubmitting}
              className="bg-brand-teal hover:bg-brand-teal/90 text-white rounded-xl px-8"
            >
              {rbSubmitting ? 'Creating...' : 'Create Weekly Booking'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Coaching Session Dialog */}
      <Dialog open={coachingSessionOpen} onOpenChange={setCoachingSessionOpen}>
        <DialogContent className="sm:max-w-[500px] max-h-[80vh] overflow-y-auto overflow-x-hidden" data-testid="coaching-session-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <GraduationCap className="w-5 h-5" style={{ color: '#0E6F6C' }} />
              Coaching Sessions
            </DialogTitle>
            <p className="text-xs text-gray-500 mt-1">
              Create shareable links for coaches. Students get ₱250/hr for 6AM–6PM slots.
            </p>
          </DialogHeader>
          
          <div className="space-y-4 py-2 w-full overflow-hidden">
            {/* Create New Session */}
            <div className="p-3 rounded-xl overflow-hidden" style={{ backgroundColor: 'rgba(14,111,108,0.12)', border: '1px solid rgba(14,111,108,0.3)' }}>
              <Label className="text-xs font-medium text-gray-600 mb-2 block">Create New Link</Label>
              <div className="flex gap-2 items-center max-w-full">
                <Input 
                  value={csCoachName}
                  onChange={(e) => setCsCoachName(e.target.value)}
                  placeholder="Coach's name"
                  className="rounded-lg text-sm h-9 flex-1 min-w-0"
                  data-testid="cs-coach-name"
                />
                <Button
                  onClick={async () => {
                    if (!csCoachName.trim()) return;
                    setCsCreating(true);
                    try {
                      const res = await createCoachingSessionApi({ coach_name: csCoachName.trim() });
                      setCoachingSessions(prev => [...prev, res.data]);
                      setCsCoachName('');
                    } catch (err) {
                      console.error('Failed to create session:', err);
                    } finally {
                      setCsCreating(false);
                    }
                  }}
                  disabled={csCreating || !csCoachName.trim()}
                  className="text-white rounded-lg h-9 px-4 flex-shrink-0 whitespace-nowrap hover:opacity-90 transition-opacity"
                  style={{ background: 'linear-gradient(145deg, #107D7B, #0E6F6C)' }}
                  data-testid="cs-create-btn"
                >
                  {csCreating ? '...' : 'Create'}
                </Button>
              </div>
            </div>
            
            {/* Existing Sessions */}
            <div>
              <Label className="text-sm font-medium text-gray-700 mb-2 block">Active Links</Label>
              {coachingSessions.length === 0 ? (
                <p className="text-xs text-gray-400 py-4 text-center">No coaching links created yet</p>
              ) : (
                <div className="space-y-2">
                  {coachingSessions.map(session => {
                    const fullUrl = `${window.location.origin}/coaching/${session.code}`;
                    const isRenaming = csRenameId === session.id;
                    return (
                      <div 
                        key={session.id}
                        className="flex items-center justify-between p-3 bg-white rounded-xl border border-gray-200"
                      >
                        <div className="flex-1 min-w-0">
                          {isRenaming ? (
                            <div className="flex items-center gap-1.5">
                              <Input
                                value={csRenameValue}
                                onChange={(e) => setCsRenameValue(e.target.value)}
                                placeholder="Coach's name"
                                className="h-8 text-sm rounded-md"
                                data-testid={`cs-rename-input-${session.id}`}
                                autoFocus
                              />
                              <Button
                                size="sm"
                                onClick={async () => {
                                  const name = csRenameValue.trim();
                                  if (!name) return;
                                  setCsRenaming(true);
                                  try {
                                    const res = await renameCoachingSessionApi(session.id, name);
                                    setCoachingSessions(prev => prev.map(s => s.id === session.id ? { ...s, coach_name: res.data.coach_name } : s));
                                    setCsRenameId(null);
                                    setCsRenameValue('');
                                  } catch (err) {
                                    console.error('Failed to rename session:', err);
                                  } finally {
                                    setCsRenaming(false);
                                  }
                                }}
                                disabled={csRenaming || !csRenameValue.trim()}
                                className="h-8 px-3 text-xs text-white hover:opacity-90"
                                style={{ background: 'linear-gradient(145deg, #107D7B, #0E6F6C)' }}
                                data-testid={`cs-rename-save-${session.id}`}
                              >
                                {csRenaming ? '...' : 'Save'}
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => { setCsRenameId(null); setCsRenameValue(''); }}
                                className="h-8 w-8 p-0"
                                data-testid={`cs-rename-cancel-${session.id}`}
                              >
                                <X className="w-4 h-4 text-gray-400" />
                              </Button>
                            </div>
                          ) : (
                            <>
                              <p className="text-sm font-medium text-gray-900">{session.coach_name}</p>
                              <p className="text-xs text-gray-400 truncate">{fullUrl}</p>
                            </>
                          )}
                        </div>
                        {!isRenaming && (
                        <div className="flex items-center gap-1 ml-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => { setCsRenameId(session.id); setCsRenameValue(session.coach_name || ''); }}
                            className="h-8 w-8 p-0"
                            data-testid={`cs-rename-${session.id}`}
                            title="Rename"
                          >
                            <Pencil className="w-4 h-4 text-gray-400" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              navigator.clipboard.writeText(fullUrl);
                              setCsCopiedId(session.id);
                              setTimeout(() => setCsCopiedId(null), 2000);
                            }}
                            className="h-8 w-8 p-0"
                            data-testid={`cs-copy-${session.id}`}
                          >
                            {csCopiedId === session.id ? (
                              <Check className="w-4 h-4 text-brand-teal" />
                            ) : (
                              <Copy className="w-4 h-4 text-gray-400" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => window.open(fullUrl, '_blank')}
                            className="h-8 w-8 p-0"
                          >
                            <ExternalLink className="w-4 h-4 text-gray-400" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={async () => {
                              try {
                                await deleteCoachingSessionApi(session.id);
                                setCoachingSessions(prev => prev.filter(s => s.id !== session.id));
                              } catch (err) {
                                console.error('Failed to delete session:', err);
                              }
                            }}
                            className="h-8 w-8 p-0 hover:text-blocks"
                          >
                            <Trash2 className="w-4 h-4 text-gray-400" />
                          </Button>
                        </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            
            {/* Info */}
            <div className="p-3 bg-gray-50 rounded-xl text-xs text-gray-500 space-y-1">
              <p><strong>How it works:</strong></p>
              <p>1. Create a link for each coach</p>
              <p>2. Share the link with the coach to give to their students</p>
              <p>3. Students book directly through the link</p>
              <p>4. 6AM–6PM bookings are charged at ₱250/hr (regular: ₱350/hr)</p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCoachingSessionOpen(false)} className="rounded-full">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Proof Viewer Dialog */}
      <PaymentProofDialog
        open={paymentProofOpen}
        onOpenChange={setPaymentProofOpen}
        playerName={paymentProofPlayer}
        proofUrl={paymentProofUrl}
      />

      {/* Cross-Day Task List Dialog */}
      <Dialog open={crossDayTasksOpen} onOpenChange={setCrossDayTasksOpen}>
        <DialogContent className="sm:max-w-md max-h-[80vh] overflow-hidden flex flex-col" data-testid="cross-day-tasks-dialog">
          <DialogHeader className="pb-2">
            <DialogTitle className="font-heading flex items-center gap-2 text-base">
              <Calendar className="w-4 h-4 text-gray-500" />
              Tasks on Other Days
              {globalAlerts?.other_day_bookings?.length > 0 && (
                <span className="text-xs font-normal text-gray-400">
                  ({globalAlerts.other_day_bookings.length})
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto -mx-6 px-6">
            {globalAlerts?.other_day_bookings?.length > 0 ? (
              <div className="space-y-1.5">
                {globalAlerts.other_day_bookings.map((booking) => {
                  // Reason tag styles based on priority/reason
                  const reasonStyles = {
                    'Overdue': { bg: 'bg-status-held-bg', text: 'text-status-held', border: 'border-status-held/20' },
                    'Hold expired': { bg: 'bg-status-held-bg', text: 'text-status-held', border: 'border-status-held/20' },
                    'Starting soon': { bg: 'bg-status-held-bg', text: 'text-status-held', border: 'border-status-held/20' },
                    'Proof uploaded': { bg: 'bg-status-review-bg', text: 'text-status-review', border: 'border-status-review/20' },
                    'Awaiting approval': { bg: 'bg-status-review-bg', text: 'text-status-review', border: 'border-status-review/20' },
                    'Unpaid': { bg: 'bg-status-expired-bg', text: 'text-status-expired', border: 'border-status-expired/20' }
                  };
                  const reasonStyle = reasonStyles[booking.reason] || reasonStyles['Unpaid'];
                  
                  // Row border color based on alert type
                  const rowBorder = booking.alert_type === 'blocking_issue' 
                    ? 'border-l-orange-400' 
                    : booking.alert_type === 'pending_approval'
                      ? 'border-l-status-review'
                      : 'border-l-status-held';
                  
                  return (
                    <div
                      key={booking.id}
                      className={`py-2.5 px-3 rounded-lg bg-white border border-gray-100 border-l-[3px] ${rowBorder} hover:bg-gray-50 transition-colors`}
                      data-testid={`cross-day-task-${booking.id}`}
                    >
                      {/* Top row: Player name + Reason tag */}
                      <div className="flex items-center justify-between gap-2 mb-1.5">
                        <span className="text-sm font-medium text-gray-900 truncate">{booking.player_name}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded border ${reasonStyle.bg} ${reasonStyle.text} ${reasonStyle.border} font-medium whitespace-nowrap`}>
                          {booking.reason}
                        </span>
                      </div>
                      
                      {/* Middle row: Date, time, court */}
                      <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
                        <span className="font-medium text-gray-700">{booking.date}</span>
                        <span>•</span>
                        <span>{formatHour(booking.start_hour)}–{formatHour(booking.end_hour)}</span>
                        <span>•</span>
                        <span>{booking.court_name}</span>
                      </div>
                      
                      {/* Bottom row: Action buttons */}
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            // Store booking ID to highlight after data loads (ref-based, no race condition)
                            pendingAnchorRef.current = booking.id;
                            // Navigate to date - the useEffect will handle scrolling after data loads
                            setSelectedDate(booking.date);
                            setCrossDayTasksOpen(false);
                          }}
                          className="flex-1 flex items-center justify-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-white bg-brand-teal hover:bg-brand-teal-active rounded-md transition-colors"
                          data-testid={`go-to-date-${booking.id}`}
                        >
                          <ChevronRight className="w-3 h-3" />
                          Go to date
                        </button>
                        <button
                          onClick={() => {
                            // Store booking ID to open drawer after data loads (ref-based, no race condition)
                            pendingDrawerBookingRef.current = booking.id;
                            // Navigate to date - the useEffect will handle opening drawer after data loads
                            setSelectedDate(booking.date);
                            setCrossDayTasksOpen(false);
                          }}
                          className="px-2.5 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                          data-testid={`open-details-${booking.id}`}
                        >
                          Details
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <CircleCheck className="w-10 h-10 mx-auto mb-2 text-brand-teal/40" />
                <p className="font-medium text-gray-700">All caught up!</p>
                <p className="text-xs mt-0.5">No pending tasks on other days</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Tournament Dialog */}
      <Dialog open={tournamentOpen} onOpenChange={setTournamentOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="tournament-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg flex items-center gap-2">
              <Trophy className="w-5 h-5" style={{ color: '#D97706' }} /> Create Tournament
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* Tournament Info */}
            <div>
              <Label className="text-sm text-gray-600">Tournament Name <span className="text-status-held">*</span></Label>
              <Input data-testid="t-name" value={tName} onChange={(e) => setTName(e.target.value)} placeholder="e.g. TP Pickleball Cup 2026" className="rounded-xl mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm text-gray-600">Organizer Name <span className="text-status-held">*</span></Label>
                <Input data-testid="t-organizer" value={tOrganizerName} onChange={(e) => setTOrganizerName(e.target.value)} placeholder="Name" className="rounded-xl mt-1" />
              </div>
              <div>
                <Label className="text-sm text-gray-600">Phone</Label>
                <Input data-testid="t-phone" value={tOrganizerPhone} onChange={(e) => setTOrganizerPhone(e.target.value)} placeholder="09XX XXX XXXX" className="rounded-xl mt-1" />
              </div>
            </div>
            <div>
              <Label className="text-sm text-gray-600">Organizer Email</Label>
              <Input data-testid="t-email" type="email" value={tOrganizerEmail} onChange={(e) => setTOrganizerEmail(e.target.value)} placeholder="email@example.com" className="rounded-xl mt-1" />
            </div>

            {/* Courts */}
            <div>
              <Label className="text-sm text-gray-600">Courts <span className="text-status-held">*</span></Label>
              <div className="flex gap-2 flex-wrap mt-1.5">
                {courts.map(court => (
                  <button
                    key={court.id}
                    data-testid={`t-court-${court.id}`}
                    onClick={() => setTCourts(prev => prev.includes(court.id) ? prev.filter(c => c !== court.id) : [...prev, court.id])}
                    className={`px-4 py-2 rounded-xl text-sm font-medium transition-all border ${
                      tCourts.includes(court.id) 
                        ? 'text-white border-amber-600' 
                        : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                    style={tCourts.includes(court.id) ? { backgroundColor: '#D97706', borderColor: '#D97706' } : {}}
                    type="button"
                  >
                    {court.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Date Range */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm text-gray-600">Start Date <span className="text-status-held">*</span></Label>
                <Input type="date" data-testid="t-start-date" value={tStartDate} onChange={(e) => setTStartDate(e.target.value)} className="rounded-xl mt-1" />
              </div>
              <div>
                <Label className="text-sm text-gray-600">End Date <span className="text-status-held">*</span></Label>
                <Input type="date" data-testid="t-end-date" value={tEndDate} onChange={(e) => setTEndDate(e.target.value)} className="rounded-xl mt-1" />
              </div>
            </div>

            {/* Time */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm text-gray-600">Start Time</Label>
                <Select value={tStartHour} onValueChange={setTStartHour}>
                  <SelectTrigger className="rounded-xl mt-1" data-testid="t-start-hour"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {hours.map(h => <SelectItem key={h} value={String(h)}>{formatHour(h)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm text-gray-600">End Time</Label>
                <Select value={tEndHour} onValueChange={setTEndHour}>
                  <SelectTrigger className="rounded-xl mt-1" data-testid="t-end-hour"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {hours.filter(h => h > parseInt(tStartHour)).map(h => <SelectItem key={h} value={String(h)}>{formatHour(h)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Cost Breakdown */}
            {tCourts.length > 0 && tStartDate && tEndDate && tStartDate <= tEndDate && (() => {
              const hrsPerDay = parseInt(tEndHour) - parseInt(tStartHour);
              const startDt = new Date(tStartDate + 'T00:00:00');
              const endDt = new Date(tEndDate + 'T00:00:00');
              const numDays = Math.floor((endDt - startDt) / (1000 * 60 * 60 * 24)) + 1;
              const rate = timelineData?.facility_rules?.hourly_rate || 350;
              const courtCost = rate * hrsPerDay * tCourts.length * numDays;
              const addlTotal = tAdditionalCharges.reduce((sum, c) => sum + (parseFloat(c.amount) || 0), 0);
              return (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-2" data-testid="t-cost-breakdown">
                  <p className="text-sm font-semibold text-amber-900">Cost Breakdown</p>
                  <div className="text-xs text-amber-800 space-y-1">
                    <div className="flex justify-between">
                      <span>{tCourts.length} court{tCourts.length > 1 ? 's' : ''} x {hrsPerDay}hr x {numDays} day{numDays > 1 ? 's' : ''} x ₱{rate}/hr</span>
                      <span className="font-medium">₱{courtCost.toLocaleString()}</span>
                    </div>
                    {tAdditionalCharges.filter(c => c.name && c.amount > 0).map((c, i) => (
                      <div key={i} className="flex justify-between">
                        <span>{c.name}</span>
                        <span className="font-medium">₱{parseFloat(c.amount).toLocaleString()}</span>
                      </div>
                    ))}
                    <div className="flex justify-between pt-2 border-t border-amber-300 text-sm font-bold text-amber-900">
                      <span>Total</span>
                      <span>₱{(courtCost + addlTotal).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Additional Charges */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <Label className="text-sm text-gray-600">Additional Charges</Label>
                <button
                  type="button"
                  onClick={() => setTAdditionalCharges(prev => [...prev, { name: '', amount: '' }])}
                  className="text-xs text-amber-700 hover:text-amber-800 font-medium flex items-center gap-1"
                  data-testid="t-add-charge"
                >
                  <Plus className="w-3 h-3" /> Add Charge
                </button>
              </div>
              {tAdditionalCharges.map((charge, i) => (
                <div key={i} className="flex gap-2 mb-2 items-center">
                  <Input
                    value={charge.name}
                    onChange={(e) => {
                      const updated = [...tAdditionalCharges];
                      updated[i] = { ...updated[i], name: e.target.value };
                      setTAdditionalCharges(updated);
                    }}
                    placeholder="Charge name (e.g. Setup fee)"
                    className="rounded-xl flex-1 h-9 text-sm"
                    data-testid={`t-charge-name-${i}`}
                  />
                  <div className="relative">
                    <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-400">₱</span>
                    <Input
                      type="number"
                      value={charge.amount}
                      onChange={(e) => {
                        const updated = [...tAdditionalCharges];
                        updated[i] = { ...updated[i], amount: e.target.value };
                        setTAdditionalCharges(updated);
                      }}
                      placeholder="0"
                      className="rounded-xl w-28 h-9 text-sm pl-6"
                      data-testid={`t-charge-amount-${i}`}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => setTAdditionalCharges(prev => prev.filter((_, j) => j !== i))}
                    className="text-gray-400 hover:text-blocks p-1"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>

            {/* Notes */}
            <div>
              <Label className="text-sm text-gray-600">Notes</Label>
              <Input data-testid="t-notes" value={tNotes} onChange={(e) => setTNotes(e.target.value)} placeholder="Any additional notes" className="rounded-xl mt-1" />
            </div>
          </div>
          <DialogFooter>
            <Button
              data-testid="t-submit-btn"
              onClick={handleCreateTournament}
              disabled={tSubmitting}
              className="text-white rounded-full px-8"
              style={{ backgroundColor: '#D97706' }}
            >
              {tSubmitting ? 'Creating...' : 'Create Tournament'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
